<template>
    <div class="ai-prompt-home">
        <!-- 頂部導航 -->
        <div class="top-nav">
            <div class="nav-left">
                <!-- 漢堡按鈕（中螢幕顯示） -->
                <button 
                    class="hamburger-btn"
                    @click="toggleSidebar"
                    :title="$t('aiprompt.toggleSidebar')"
                >
                    <svg-icon icon-class="hamburger" class="hamburger-icon" />
                </button>
                <h1 class="title">{{ $t('aiprompt.title') }}</h1>
            </div>
            <div class="nav-right">
                <!-- 未登入狀態 -->
                <div v-if="!isLogin" class="login-section">
                    <button class="login-btn" @click="loginTMID">
                        {{ $t('aiprompt.login') }}
                    </button>
                </div>
                
                <!-- 已登入狀態 -->
                <div v-else class="user-section">
                    <div class="user-info" @click="toggleUserMenu">
                        <img v-if="profile.picture" :src="profile.picture" class="avatar" alt="avatar">
                        <div v-else class="avatar-placeholder">{{ profile.name?.charAt(0) }}</div>
                        <span class="user-name">{{ profile.name }}</span>
                    </div>
                    
                    <!-- 用戶選單 -->
                    <div v-show="showUserMenu" class="user-menu">
                        <div class="menu-item">{{ profile.name }}</div>
                        <div class="menu-divider"></div>
                        
                        <!-- 管理員入口 (只有特定用戶才能看到) -->
                        <div v-if="isAdminUser" class="menu-item menu-action admin-entry" @click="goToAdminLogin">
                            {{ $t('aiprompt.adminEntry') }}
                        </div>
                        <div v-if="isAdminUser" class="menu-divider"></div>
                        
                        <div class="menu-item menu-action" @click="logout">
                            {{ $t('aiprompt.logout') }}
                        </div>
                    </div>
                </div>
            </div>
        </div>


        <!-- 主要內容區域 -->
        <div class="main-content">
            <div class="content-layout">
                <!-- 左側管理面板 -->
                <div class="left-panel" :class="{ 'hidden': !showSidebar }">
                    <div class="panel-header">
                        <h3>{{ $t('aiprompt.dishManagement') }}</h3>
                    </div>
                    
                    <!-- 官方餐盤 -->
                    <div class="collection-section">
                        <div
                            class="collection-item official-collection"
                            :class="{ active: currentView === 'official' }"
                            @click="switchToOfficialView"
                        >
                            <div class="collection-info">
                                <span class="collection-name">{{ $t('aiprompt.officialDish') }}</span>
                            </div>
                        </div>

                        <!-- 收藏的餐盤 -->
                        <div
                            v-if="isLogin"
                            class="collection-item-wrapper favorite-wrapper"
                        >
                            <div
                                class="collection-item favorite-collection"
                                :class="{ active: currentView === 'favorites' }"
                                @click="switchToFavoritesView"
                            >
                                <div class="collection-info">
                                    <svg-icon icon-class="star-fill" class="favorite-icon" />
                                    <span class="collection-name">{{ $t('aiprompt.favoriteDishes') }}</span>
                                </div>
                            </div>
                            <button
                                class="share-code-btn"
                                @click.stop="showShareCodeModal = true"
                                :title="$t('aiprompt.shareCodeSearch')"
                            >
                                <svg-icon icon-class="add" />
                            </button>
                        </div>
                    </div>
                    
                    <!-- 自訂餐盤 -->
                    <div class="collection-section">
                        <div class="section-header">
                            <span class="section-title">{{ $t('aiprompt.myDishes') }}</span>
                            <button class="add-collection-btn" @click="showAddCollectionDialog" :title="$t('aiprompt.addDish')">
                                <svg-icon icon-class="add" />
                            </button>
                        </div>
                        
                        <!-- 載入狀態 -->
                        <div v-if="isLoadingCollections" class="loading-collections">
                            <div class="mini-spinner"></div>
                            <span>{{ $t('aiprompt.loading') }}</span>
                        </div>
                        
                        <!-- 餐盤列表 -->
                        <div v-else-if="userCollections.length > 0" class="collections-list">
                            <div 
                                v-for="collection in userCollections" 
                                :key="collection.id"
                                class="collection-item custom-collection"
                                :class="{ active: selectedCollection?.id === collection.id }"
                                @click="selectCustomCollection(collection)"
                            >
                                <div class="collection-info">
                                    <span class="collection-name">
                                        {{ collection.tags && collection.tags.includes('default') 
                                            ? $t('aiprompt.defaultDishName') 
                                            : (collection.collectionName || collection.name) }}
                                    </span>
                                </div>
                                <div class="collection-actions">
                                    <button 
                                        class="action-btn edit-btn" 
                                        @click.stop="showEditCollectionDialog(collection)"
                                        :title="$t('aiprompt.tooltips.edit')"
                                    >
                                        <svg-icon icon-class="edit" class="edit-icon" />
                                    </button>
                                    <button 
                                        class="action-btn delete-btn" 
                                        @click.stop="deleteCollection(collection)"
                                        :title="$t('aiprompt.tooltips.delete')"
                                    >
                                        <svg-icon icon-class="trash" class="delete-icon" />
                                    </button>
                                </div>
                            </div>
                        </div>
                        
                        <!-- 無餐盤狀態 -->
                        <div v-else-if="isLogin" class="empty-collections">
                            <p>{{ $t('aiprompt.noDishesYet') }}</p>
                        </div>
                        
                        <!-- 未登入狀態 -->
                        <div v-else class="not-login-state">
                            <p>{{ $t('aiprompt.pleaseLoginFirst') }}</p>
                        </div>
                    </div>
                </div>
                
                <!-- 中間內容區域 -->
                <div class="middle-content">
                    <!-- 官方餐盤視圖 -->
                    <div v-if="currentView === 'official'">
                        <!-- 載入狀態 -->
                        <div v-if="isLoadingData" class="loading-section">
                            <div class="loading-spinner"></div>
                            <p>{{ $t('aiprompt.loadingPromptData') }}</p>
                        </div>
                        
                        <!-- AI Prompt 按鈕區域 -->
                        <div v-else-if="promptCategories.length > 0" class="prompt-categories">
                            <div class="category-header">
                                <h2><svg-icon icon-class="robot" class="robot-icon" />{{ $t('aiprompt.officialPromptDish') }}</h2>
                            </div>
                            
                            <!-- 分類篩選按鈕 -->
                            <div class="filter-section">
                                <div class="filter-buttons">
                                    <button 
                                        v-for="filter in availableFilters" 
                                        :key="filter"
                                        class="filter-btn"
                                        :class="{ active: selectedFilter === filter }"
                                        @click="applyFilter(filter)"
                                    >
                                        {{ filter === 'showAll' ? $t('aiprompt.showAll') : getCategoryLabel(filter) }}
                                        <span v-if="getFilterCount(filter) > 0" class="filter-count">
                                            {{ getFilterCount(filter) }}
                                        </span>
                                    </button>
                                </div>
                            </div>
                    
                            <div class="prompt-grid">
                                <button 
                                    v-for="category in filteredPromptCategories" 
                                    :key="category.id"
                                    :class="['prompt-button', { selected: selectedPromptForPreview?.id === category.id }]"
                                    @click="showPromptDetails(category)"
                                >
                                    {{ category.name }}
                                </button>
                            </div>
                            
                        </div>
                        
                        <!-- 無資料狀態 -->
                        <div v-else class="no-data-section">
                            <div class="no-data-icon">📄</div>
                            <h3>{{ $t('aiprompt.noPromptData') }}</h3>
                            <p>{{ $t('aiprompt.pleaseUploadFile') }}</p>
                            <button v-if="isAdminUser" @click="goToAdminLogin" class="admin-upload-btn">
                                {{ $t('aiprompt.goToAdminUpload') }}
                            </button>
                        </div>
                    </div>

                    <!-- 收藏的餐盤視圖 -->
                    <div v-else-if="currentView === 'favorites'" class="favorites-view">
                        <!-- 收藏列表視圖 -->
                        <div v-if="!favoriteDetailView">
                            <div class="category-header">
                                <h2>
                                    <svg-icon icon-class="star-fill" class="star-icon" />
                                    {{ $t('aiprompt.favoriteDishes') }}
                                </h2>
                            </div>
                            <!-- 使用路由載入收藏列表組件 -->
                            <AIPromptFavoriteList @select-favorite="selectFavoriteCollection" />
                        </div>

                        <!-- 收藏餐盤詳情視圖 -->
                        <div v-else-if="selectedFavorite" class="favorite-detail-view">
                            <div class="detail-header">
                                <button class="back-btn" @click="backToFavoritesList">
                                    <svg-icon icon-class="arrow-pre" class="back-icon" />
                                    {{ $t('aiprompt.backToFavorites') }}
                                </button>
                            </div>

                            <div class="collection-detail">
                                <div class="collection-header">
                                    <h2>{{ selectedFavorite.collection?.collectionName || selectedFavorite.CollectionName }}</h2>
                                    <p v-if="selectedFavorite.collection?.description" class="collection-description">
                                        {{ selectedFavorite.collection.description }}
                                    </p>
                                    <div class="creator-info">
                                        <span class="creator-label">{{ $t('aiprompt.creator') }}：</span>
                                        <span class="creator-name">{{ selectedFavorite.collection?.creator?.name || selectedFavorite.CreatorName }}</span>
                                    </div>
                                    <hr class="divider" />
                                </div>

                                <!-- 收藏餐盤的指令列表（唯讀） -->
                                <div class="collection-content">
                                    <div v-if="selectedFavorite.prompts && selectedFavorite.prompts.length > 0" class="prompts-list">
                                        <div class="prompts-header">
                                            <h3>{{ $t('aiprompt.promptList') }}</h3>
                                        </div>

                                        <!-- 收藏餐盤分類篩選按鈕 -->
                                        <div class="filter-section">
                                            <div class="filter-buttons">
                                                <button
                                                    class="filter-btn"
                                                    :class="{ active: favoriteSelectedFilter === 'showAll' }"
                                                    @click="applyFavoriteFilter('showAll')"
                                                >
                                                    {{ $t('aiprompt.showAll') }}
                                                    <span class="filter-count">
                                                        {{ getFavoriteFilterCount('showAll') }}
                                                    </span>
                                                </button>

                                                <button
                                                    v-for="option in categoryOptions"
                                                    v-show="getFavoriteFilterCount(option.value) > 0"
                                                    :key="option.value"
                                                    class="filter-btn"
                                                    :class="{ active: favoriteSelectedFilter === option.value }"
                                                    @click="applyFavoriteFilter(option.value)"
                                                >
                                                    {{ option.label }}
                                                    <span class="filter-count">
                                                        {{ getFavoriteFilterCount(option.value) }}
                                                    </span>
                                                </button>
                                            </div>
                                        </div>

                                        <div class="prompt-grid">
                                            <div
                                                v-for="prompt in favoriteFilteredPrompts"
                                                :key="prompt.id"
                                                :class="['prompt-card', 'readonly', { selected: selectedPromptForPreview?.id === prompt.id }]"
                                                @click="showFavoritePromptPreview(prompt)"
                                            >
                                                <div class="prompt-card-header">
                                                    <h4 class="prompt-title">{{ prompt.purpose }}</h4>
                                                </div>
                                                <div class="prompt-meta">
                                                    <span class="prompt-category">{{ getCategoryLabel(prompt.category) }}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div v-else class="empty-prompts">
                                        <p>{{ $t('aiprompt.noPromptsInCollection') }}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 自訂餐盤視圖 -->
                    <div v-else-if="currentView === 'custom'" class="custom-collection-view">
                        <div v-if="selectedCollection" class="collection-detail">
                            <div class="collection-header">
                                <h2>{{ selectedCollection.collectionName || selectedCollection.name }}</h2>
                                <p v-if="selectedCollection.description" class="collection-description">
                                    {{ selectedCollection.description }}
                                </p>
                            </div>
                            
                            <!-- 餐盤中的指令列表 -->
                            <div class="collection-content">
                                <!-- 載入指令狀態 -->
                                <div v-if="isLoadingPrompts" class="loading-prompts">
                                    <div class="mini-spinner"></div>
                                    <span>{{ $t('aiprompt.loadingPrompts') }}</span>
                                </div>
                                
                                <!-- 指令列表 -->
                                <div v-else-if="collectionPrompts.length > 0" class="prompts-list">
                                    <div class="prompts-header">
                                        <h3>{{ $t('aiprompt.promptList') }}</h3>
                                        <div class="header-actions">
                                            <button class="add-prompt-btn" @click="openEditDishModal">
                                              {{ $t('aiprompt.editDishModal.title') }}
                                            </button>
                                            <button class="add-prompt-btn" @click="showAddPromptDialog">
                                                {{ $t('aiprompt.addPrompt') }}
                                            </button>
                                        </div>
                                    </div>
                                    
                                    <!-- 自訂餐盤分類篩選按鈕 -->
                                    <div class="filter-section">
                                        <div class="filter-buttons">
                                            <button 
                                                class="filter-btn"
                                                :class="{ active: customSelectedFilter === 'showAll' }"
                                                @click="applyCustomFilter('showAll')"
                                            >
                                                {{ $t('aiprompt.showAll') }}
                                                <span class="filter-count">
                                                    {{ collectionPrompts.length }}
                                                </span>
                                            </button>
                                            
                                            <button 
                                                v-for="option in categoryOptions" 
                                                v-show="getCustomFilterCount(option.value) > 0"
                                                :key="option.value"
                                                class="filter-btn"
                                                :class="{ active: customSelectedFilter === option.value }"
                                                @click="applyCustomFilter(option.value)"
                                            >
                                                {{ option.label }}
                                                <span class="filter-count">
                                                    {{ getCustomFilterCount(option.value) }}
                                                </span>
                                            </button>
                                        </div>
                                    </div>
                                    
                                    <div class="prompt-grid">
                                        <div 
                                            v-for="prompt in customFilteredPrompts" 
                                            :key="prompt.id"
                                            :class="['prompt-card', { selected: selectedPromptForPreview?.id === prompt.id }]"
                                            @click="showCustomPromptPreview(prompt)"
                                        >
                                            <div class="prompt-card-header">
                                                <h4 class="prompt-title">{{ prompt.purpose }}</h4>
                                                <div class="prompt-actions">
                                                    <button 
                                                        class="action-btn edit-btn" 
                                                        @click="editPrompt(prompt)"
                                                        :title="$t('aiprompt.edit')"
                                                    >
                                                        <svg-icon icon-class="edit" />
                                                    </button>
                                                    <button 
                                                        class="action-btn delete-btn" 
                                                        @click="deletePrompt(prompt)"
                                                        :title="$t('aiprompt.delete')"
                                                    >
                                                        <svg-icon icon-class="trash" />
                                                    </button>
                                                </div>
                                            </div>
                                            <div class="prompt-meta">
                                                <span class="prompt-category">{{ getCategoryLabel(prompt.category) }}</span>
                                                <!-- <span class="prompt-creator">by {{ prompt.creator.name }}</span> -->
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                <!-- 空指令狀態 -->
                                <div v-else class="empty-collection">
                                    <div class="empty-icon">📝</div>
                                    <h3>{{ $t('aiprompt.emptyDishTitle') }}</h3>
                                    <p>{{ $t('aiprompt.emptyDishHint') }}</p>
                                    <button class="add-prompt-btn" @click="showAddPromptDialog">
                                        {{ $t('aiprompt.addFirstPrompt') }}
                                    </button>
                                </div>
                            </div>
                        </div>
                        
                        <!-- 未選擇餐盤 -->
                        <div v-else class="no-selection">
                            <div class="no-selection-icon">👈</div>
                            <h3>{{ $t('aiprompt.noDishSelected') }}</h3>
                            <p>{{ $t('aiprompt.selectDishHint') }}</p>
                        </div>
                    </div>
                </div>
                
                <!-- 右側預覽面板（大螢幕時顯示） -->
                <div v-if="selectedPromptForPreview && !shouldShowPreviewAsModal" class="right-preview-panel show" style="width: 380px; min-width: 380px;">
                    <div class="preview-content">
                        <!-- 預覽標題欄 -->
                        <div class="preview-header">
                            <h3 class="preview-title">{{ selectedPromptForPreview.name || selectedPromptForPreview.purpose || $t('aiprompt.promptDetails') }}</h3>
                            <button class="close-preview-btn" @click="closePreview">
                                <svg-icon icon-class="close" />
                            </button>
                        </div>
                        
                        <!-- 預覽內容 -->
                        <div class="preview-body">
                            <!-- 官方餐盤指令預覽 -->
                            <div v-if="currentView === 'official'" class="official-prompt-preview">
                                <!-- 指令分類欄位 -->
                                <div class="field-group">
                                    <label class="field-label">{{ $t('aiprompt.promptCategory') }}</label>
                                    <div class="field-value">{{ getOfficialCategoryLabel() }}</div>
                                </div>
                                
                                <div v-for="(item, index) in selectedPromptForPreview.data" :key="index" class="prompt-item">
                                    <div class="item-fields">
                                        <div class="field-group" v-for="[key, value] in getFilteredItemEntries(item)" :key="key">
                                            <label class="field-label">{{ key }}</label>
                                            <div class="field-value">
                                                <template v-if="key.toLowerCase().includes('sourcedatatype') || key.includes('來源資料類型') || key.includes('来源数据类型')">
                                                    {{ value === 'text' || value === 'Text' ? $t('aiprompt.addPromptModal.sourceDataTypeText') : $t('aiprompt.addPromptModal.sourceDataTypeImage') }}
                                                </template>
                                                <template v-else>
                                                    {{ value }}
                                                </template>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <!-- 自訂餐盤指令預覽 -->
                            <div v-if="currentView === 'custom'" class="custom-prompt-preview">
                                <div class="field-group">
                                    <label class="field-label">{{ $t('aiprompt.promptCategory') }}</label>
                                    <div class="field-value">{{ getCategoryLabel(selectedPromptForPreview.category) }}</div>
                                </div>
                                
                                <div class="field-group">
                                    <label class="field-label">{{ $t('aiprompt.promptPurpose') }}</label>
                                    <div class="field-value">{{ selectedPromptForPreview.purpose }}</div>
                                </div>
                                
                                <div v-if="selectedPromptForPreview.usage" class="field-group">
                                    <label class="field-label">{{ $t('aiprompt.usage') }}</label>
                                    <div class="field-value">{{ selectedPromptForPreview.usage }}</div>
                                </div>
                                
                                <div v-if="selectedPromptForPreview.sourceData" class="field-group">
                                    <label class="field-label">{{ $t('aiprompt.sourceData') }}</label>
                                    <div class="field-value">{{ selectedPromptForPreview.sourceData }}</div>
                                </div>

                                <div class="field-group">
                                    <label class="field-label">{{ $t('aiprompt.shareToPlate.previewLabels.sourceDataType') }}</label>
                                    <div class="field-value">{{ (selectedPromptForPreview.sourceDataType || 'text') === 'text' ? $t('aiprompt.addPromptModal.sourceDataTypeText') : $t('aiprompt.addPromptModal.sourceDataTypeImage') }}</div>
                                </div>

                                <div class="field-group">
                                    <label class="field-label">{{ $t('aiprompt.conditionalPrompt') }}</label>
                                    <div class="field-value prompt-content">{{ selectedPromptForPreview.conditionalPrompt }}</div>
                                </div>
                                
                                <div v-if="selectedPromptForPreview.expectedOutput" class="field-group">
                                    <label class="field-label">{{ $t('aiprompt.expectedOutput') }}</label>
                                    <div class="field-value">{{ selectedPromptForPreview.expectedOutput }}</div>
                                </div>
                                
                                <div v-if="selectedPromptForPreview.followUpActions" class="field-group">
                                    <label class="field-label">{{ $t('aiprompt.followUpActions') }}</label>
                                    <div class="field-value">{{ selectedPromptForPreview.followUpActions }}</div>
                                </div>
                                
                                <div v-if="selectedPromptForPreview.personalNotes" class="field-group">
                                    <label class="field-label">{{ $t('aiprompt.personalNotes') }}</label>
                                    <div class="field-value">{{ selectedPromptForPreview.personalNotes }}</div>
                                </div>
                                
                                <div class="field-group">
                                    <label class="field-label">{{ $t('aiprompt.creator') }}</label>
                                    <div class="field-value">{{ selectedPromptForPreview.creator?.name || $t('aiprompt.unknown') }}</div>
                                </div>
                            </div>

                            <!-- 收藏餐盤指令預覽 -->
                            <div v-if="currentView === 'favorites'" class="favorite-prompt-preview">
                                <div class="field-group">
                                    <label class="field-label">{{ $t('aiprompt.promptCategory') }}</label>
                                    <div class="field-value">{{ getCategoryLabel(selectedPromptForPreview.category) }}</div>
                                </div>

                                <div class="field-group">
                                    <label class="field-label">{{ $t('aiprompt.promptPurpose') }}</label>
                                    <div class="field-value">{{ selectedPromptForPreview.purpose }}</div>
                                </div>

                                <div v-if="selectedPromptForPreview.usage" class="field-group">
                                    <label class="field-label">{{ $t('aiprompt.usage') }}</label>
                                    <div class="field-value">{{ selectedPromptForPreview.usage }}</div>
                                </div>

                                <div v-if="selectedPromptForPreview.sourceData" class="field-group">
                                    <label class="field-label">{{ $t('aiprompt.sourceData') }}</label>
                                    <div class="field-value">{{ selectedPromptForPreview.sourceData }}</div>
                                </div>

                                <div class="field-group">
                                    <label class="field-label">{{ $t('aiprompt.conditionalPrompt') }}</label>
                                    <div class="field-value prompt-content">{{ selectedPromptForPreview.conditionalPrompt }}</div>
                                </div>

                                <div v-if="selectedPromptForPreview.expectedOutput" class="field-group">
                                    <label class="field-label">{{ $t('aiprompt.expectedOutput') }}</label>
                                    <div class="field-value">{{ selectedPromptForPreview.expectedOutput }}</div>
                                </div>

                                <div v-if="selectedPromptForPreview.followUpActions" class="field-group">
                                    <label class="field-label">{{ $t('aiprompt.followUpActions') }}</label>
                                    <div class="field-value">{{ selectedPromptForPreview.followUpActions }}</div>
                                </div>

                                <div v-if="selectedPromptForPreview.personalNotes" class="field-group">
                                    <label class="field-label">{{ $t('aiprompt.personalNotes') }}</label>
                                    <div class="field-value">{{ selectedPromptForPreview.personalNotes }}</div>
                                </div>

                                <div class="field-group">
                                    <label class="field-label">{{ $t('aiprompt.creator') }}</label>
                                    <div class="field-value">{{ selectedPromptForPreview.creator?.name || $t('aiprompt.unknown') }}</div>
                                </div>
                            </div>

                            <!-- 簡碼搜尋餐盤指令預覽 -->
                            <div v-if="currentView === 'shareCode'" class="share-code-prompt-preview">
                                <div class="field-group">
                                    <label class="field-label">{{ $t('aiprompt.promptCategory') }}</label>
                                    <div class="field-value">{{ getCategoryLabel(selectedPromptForPreview.category) }}</div>
                                </div>

                                <div class="field-group">
                                    <label class="field-label">{{ $t('aiprompt.promptPurpose') }}</label>
                                    <div class="field-value">{{ selectedPromptForPreview.purpose }}</div>
                                </div>

                                <div v-if="selectedPromptForPreview.usage" class="field-group">
                                    <label class="field-label">{{ $t('aiprompt.usage') }}</label>
                                    <div class="field-value">{{ selectedPromptForPreview.usage }}</div>
                                </div>

                                <div v-if="selectedPromptForPreview.sourceData" class="field-group">
                                    <label class="field-label">{{ $t('aiprompt.sourceData') }}</label>
                                    <div class="field-value">{{ selectedPromptForPreview.sourceData }}</div>
                                </div>

                                <div class="field-group">
                                    <label class="field-label">{{ $t('aiprompt.conditionalPrompt') }}</label>
                                    <div class="field-value prompt-content">{{ selectedPromptForPreview.conditionalPrompt }}</div>
                                </div>

                                <div v-if="selectedPromptForPreview.expectedOutput" class="field-group">
                                    <label class="field-label">{{ $t('aiprompt.expectedOutput') }}</label>
                                    <div class="field-value">{{ selectedPromptForPreview.expectedOutput }}</div>
                                </div>

                                <div v-if="selectedPromptForPreview.followUpActions" class="field-group">
                                    <label class="field-label">{{ $t('aiprompt.followUpActions') }}</label>
                                    <div class="field-value">{{ selectedPromptForPreview.followUpActions }}</div>
                                </div>

                                <div v-if="selectedPromptForPreview.personalNotes" class="field-group">
                                    <label class="field-label">{{ $t('aiprompt.personalNotes') }}</label>
                                    <div class="field-value">{{ selectedPromptForPreview.personalNotes }}</div>
                                </div>

                                <div class="field-group">
                                    <label class="field-label">{{ $t('aiprompt.creator') }}</label>
                                    <div class="field-value">{{ selectedPromptForPreview.creator?.name || $t('aiprompt.unknown') }}</div>
                                </div>
                            </div>
                        </div>
                        
                        <!-- 預覽操作欄 -->
                        <div class="preview-actions">
                            <button v-if="currentView === 'custom'" class="action-btn edit-btn" @click="editPromptFromPreview">
                                <svg-icon icon-class="edit" />
                                {{ $t('aiprompt.editPrompt') }}
                            </button>
                            <button class="action-btn copy-btn" @click="copyPromptContent">
                                <svg-icon icon-class="copy" />
                                {{ $t('aiprompt.sendToHT') }}
                            </button>
                        </div>
                    </div>
                    
                </div>
            </div>
        </div>
        
        <!-- 全屏模態預覽面板（中螢幕時顯示） -->
        <div v-if="shouldShowPreviewAsModal" class="modal-overlay preview-modal">
            <div class="modal-content preview-modal-content" @click.stop>
                <!-- 預覽標題欄 -->
                <div class="modal-header">
                    <h3>{{ selectedPromptForPreview.name || selectedPromptForPreview.purpose || $t('aiprompt.promptDetails') }}</h3>
                    <button class="close-btn" @click="closePreview">
                        <svg-icon icon-class="close" />
                    </button>
                </div>
                
                <!-- 預覽內容 -->
                <div class="modal-body preview-modal-body">
                    <!-- 官方餐盤指令預覽 -->
                    <div v-if="currentView === 'official'" class="official-prompt-preview">
                        <!-- 指令分類欄位 -->
                        <div class="field-group">
                            <label class="field-label">{{ $t('aiprompt.promptCategory') }}</label>
                            <div class="field-value">{{ getOfficialCategoryLabel() }}</div>
                        </div>
                        
                        <div v-for="(item, index) in selectedPromptForPreview.data" :key="index" class="prompt-item">
                            <div class="item-fields">
                                <div class="field-group" v-for="[key, value] in getFilteredItemEntries(item)" :key="key">
                                    <label class="field-label">{{ key }}</label>
                                    <div class="field-value" :class="{ 'prompt-content': key === '指令用途' || key === 'Conditional Prompt' || key === '條件指令' }">
                                        <template v-if="key.toLowerCase().includes('sourcedatatype') || key.includes('來源資料類型') || key.includes('来源数据类型')">
                                            {{ value === 'text' || value === 'Text' ? $t('aiprompt.addPromptModal.sourceDataTypeText') : $t('aiprompt.addPromptModal.sourceDataTypeImage') }}
                                        </template>
                                        <template v-else>
                                            {{ value }}
                                        </template>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- 自訂餐盤指令預覽 -->
                    <div v-if="currentView === 'custom'" class="custom-prompt-preview">
                        <div class="field-group">
                            <label class="field-label">{{ $t('aiprompt.promptCategory') }}</label>
                            <div class="field-value">{{ getCategoryLabel(selectedPromptForPreview.category) }}</div>
                        </div>

                        <div class="field-group">
                            <label class="field-label">{{ $t('aiprompt.promptPurpose') }}</label>
                            <div class="field-value">{{ selectedPromptForPreview.purpose }}</div>
                        </div>

                        <div v-if="selectedPromptForPreview.usage" class="field-group">
                            <label class="field-label">{{ $t('aiprompt.usage') }}</label>
                            <div class="field-value">{{ selectedPromptForPreview.usage }}</div>
                        </div>

                        <div v-if="selectedPromptForPreview.sourceData" class="field-group">
                            <label class="field-label">{{ $t('aiprompt.sourceData') }}</label>
                            <div class="field-value">{{ selectedPromptForPreview.sourceData }}</div>
                        </div>

                        <div v-if="selectedPromptForPreview.sourceDataType" class="field-group">
                            <label class="field-label">{{ $t('aiprompt.shareToPlate.previewLabels.sourceDataType') }}</label>
                            <div class="field-value">{{ selectedPromptForPreview.sourceDataType === 'text' ? $t('aiprompt.addPromptModal.sourceDataTypeText') : $t('aiprompt.addPromptModal.sourceDataTypeImage') }}</div>
                        </div>

                        <div class="field-group">
                            <label class="field-label">{{ $t('aiprompt.conditionalPrompt') }}</label>
                            <div class="field-value prompt-content">{{ selectedPromptForPreview.conditionalPrompt }}</div>
                        </div>

                        <div v-if="selectedPromptForPreview.expectedOutput" class="field-group">
                            <label class="field-label">{{ $t('aiprompt.expectedOutput') }}</label>
                            <div class="field-value">{{ selectedPromptForPreview.expectedOutput }}</div>
                        </div>

                        <div v-if="selectedPromptForPreview.followUpActions" class="field-group">
                            <label class="field-label">{{ $t('aiprompt.followUpActions') }}</label>
                            <div class="field-value">{{ selectedPromptForPreview.followUpActions }}</div>
                        </div>

                        <div v-if="selectedPromptForPreview.personalNotes" class="field-group">
                            <label class="field-label">{{ $t('aiprompt.personalNotes') }}</label>
                            <div class="field-value">{{ selectedPromptForPreview.personalNotes }}</div>
                        </div>

                        <div class="field-group">
                            <label class="field-label">{{ $t('aiprompt.creator') }}</label>
                            <div class="field-value">{{ selectedPromptForPreview.creator?.name || $t('aiprompt.unknown') }}</div>
                        </div>
                    </div>

                    <!-- 簡碼搜尋餐盤指令預覽 -->
                    <div v-if="currentView === 'shareCode'" class="custom-prompt-preview">
                        <div class="field-group">
                            <label class="field-label">{{ $t('aiprompt.promptCategory') }}</label>
                            <div class="field-value">{{ getCategoryLabel(selectedPromptForPreview.category) }}</div>
                        </div>

                        <div class="field-group">
                            <label class="field-label">{{ $t('aiprompt.promptPurpose') }}</label>
                            <div class="field-value">{{ selectedPromptForPreview.purpose }}</div>
                        </div>

                        <div v-if="selectedPromptForPreview.usage" class="field-group">
                            <label class="field-label">{{ $t('aiprompt.usage') }}</label>
                            <div class="field-value">{{ selectedPromptForPreview.usage }}</div>
                        </div>

                        <div v-if="selectedPromptForPreview.sourceData" class="field-group">
                            <label class="field-label">{{ $t('aiprompt.sourceData') }}</label>
                            <div class="field-value">{{ selectedPromptForPreview.sourceData }}</div>
                        </div>

                        <div v-if="selectedPromptForPreview.sourceDataType" class="field-group">
                            <label class="field-label">{{ $t('aiprompt.shareToPlate.previewLabels.sourceDataType') }}</label>
                            <div class="field-value">{{ selectedPromptForPreview.sourceDataType === 'text' ? $t('aiprompt.addPromptModal.sourceDataTypeText') : $t('aiprompt.addPromptModal.sourceDataTypeImage') }}</div>
                        </div>

                        <div class="field-group">
                            <label class="field-label">{{ $t('aiprompt.conditionalPrompt') }}</label>
                            <div class="field-value prompt-content">{{ selectedPromptForPreview.conditionalPrompt }}</div>
                        </div>

                        <div v-if="selectedPromptForPreview.expectedOutput" class="field-group">
                            <label class="field-label">{{ $t('aiprompt.expectedOutput') }}</label>
                            <div class="field-value">{{ selectedPromptForPreview.expectedOutput }}</div>
                        </div>

                        <div v-if="selectedPromptForPreview.followUpActions" class="field-group">
                            <label class="field-label">{{ $t('aiprompt.followUpActions') }}</label>
                            <div class="field-value">{{ selectedPromptForPreview.followUpActions }}</div>
                        </div>

                        <div v-if="selectedPromptForPreview.personalNotes" class="field-group">
                            <label class="field-label">{{ $t('aiprompt.personalNotes') }}</label>
                            <div class="field-value">{{ selectedPromptForPreview.personalNotes }}</div>
                        </div>

                        <div class="field-group">
                            <label class="field-label">{{ $t('aiprompt.creator') }}</label>
                            <div class="field-value">{{ selectedPromptForPreview.creator?.name || $t('aiprompt.unknown') }}</div>
                        </div>
                    </div>
                </div>
                
                <!-- 預覽操作欄 -->
                <div class="modal-footer preview-actions">
                    <button v-if="currentView === 'custom'" class="btn-cancel edit-btn" @click="editPromptFromPreview">
                        <svg-icon icon-class="edit" />
                        {{ $t('aiprompt.editPrompt') }}
                    </button>
                    <button class="btn-confirm copy-btn" @click="copyPromptContent">
                        <svg-icon icon-class="copy" />
                        {{ $t('aiprompt.sendToHT') }}
                    </button>
                </div>
            </div>
        </div>
        
        <!-- 新增餐盤彈出表單 -->
        <div v-show="showAddCollectionForm" class="modal-overlay" @click="closeAddCollectionDialog">
            <div class="modal-content" @click.stop>
                <div class="modal-header">
                    <h3>{{ $t('aiprompt.addDishModal.title') }}</h3>
                    <button class="close-btn" @click="closeAddCollectionDialog">
                        <svg-icon icon-class="close" />
                    </button>
                </div>
                
                <div class="modal-body">
                    <div class="form-group">
                        <label for="collectionName">
                            {{ newCollection.isFirstTime ? $t('aiprompt.editDishModal.defaultNameNotEditable') : $t('aiprompt.addDishModal.nameRequired') }}
                        </label>
                        <input 
                            id="collectionName"
                            :value="newCollection.isFirstTime ? $t('aiprompt.defaultDishName') : newCollection.name"
                            @input="!newCollection.isFirstTime && (newCollection.name = $event.target.value)"
                            type="text" 
                            :placeholder="$t('aiprompt.addDishModal.namePlaceholder')"
                            class="form-input"
                            :class="{ 'disabled': newCollection.isFirstTime }"
                            :disabled="newCollection.isFirstTime"
                            maxlength="50"
                        />
                    </div>
                    
                    <div class="form-group">
                        <label for="collectionDescription">{{ $t('aiprompt.addDishModal.descriptionLabel') }}</label>
                        <textarea 
                            id="collectionDescription"
                            v-model="newCollection.description"
                            :placeholder="$t('aiprompt.addDishModal.descriptionPlaceholder')"
                            class="form-textarea"
                            rows="3"
                            maxlength="200"
                        ></textarea>
                    </div>
                    
                   
                </div>
                
                <div class="modal-footer">
                    <button @click="closeAddCollectionDialog" class="btn-cancel">
                        {{ $t('aiprompt.cancel') }}
                    </button>
                    <button 
                        @click="createCollection" 
                        class="btn-confirm"
                        :disabled="(!newCollection.isFirstTime && !newCollection.name.trim()) || isCreatingCollection"
                    >
                        {{ isCreatingCollection ? $t('aiprompt.addDishModal.creating') : $t('aiprompt.addDishModal.confirmCreate') }}
                    </button>
                </div>
            </div>
        </div>
        
        <!-- 編輯餐盤彈出表單 -->
        <div v-show="showEditCollectionForm" class="modal-overlay" @click="closeEditCollectionDialog">
            <div class="modal-content" @click.stop>
                <div class="modal-header">
                    <h3>{{ $t('aiprompt.editDishModal.title') }}</h3>
                    <button class="close-btn" @click="closeEditCollectionDialog">
                        <svg-icon icon-class="close" />
                    </button>
                </div>
                
                <div class="modal-body">
                    <div class="form-group">
                        <label for="editCollectionName">{{ editingCollection.isDefault ? $t('aiprompt.editDishModal.defaultNameNotEditable') : $t('aiprompt.addDishModal.nameRequired') }}
                        </label>
                        <input 
                            id="editCollectionName"
                            :value="editingCollection.isDefault ? $t('aiprompt.defaultDishName') : editingCollection.name"
                            @input="!editingCollection.isDefault && (editingCollection.name = $event.target.value)"
                            type="text" 
                            :placeholder="$t('aiprompt.addDishModal.namePlaceholder')"
                            class="form-input"
                            :class="{ 'disabled': editingCollection.isDefault }"
                            :disabled="editingCollection.isDefault"
                            maxlength="50"
                        />
                       
                    </div>
                    
                    <div class="form-group">
                        <label for="editCollectionDescription">{{ $t('aiprompt.addDishModal.descriptionLabel') }}</label>
                        <textarea 
                            id="editCollectionDescription"
                            v-model="editingCollection.description"
                            :placeholder="$t('aiprompt.addDishModal.descriptionPlaceholder')"
                            class="form-textarea"
                            rows="3"
                            maxlength="200"
                        ></textarea>
                    </div>
                    
                    <!-- 分享功能區塊 -->
                    <div class="form-group sharing-section">
                        <label class="section-label">{{ $t('aiprompt.editDishModal.dishSharing') }}</label>
                        
                        <!-- 未發布狀態 - 顯示發布按鈕 -->
                        <div v-if="!editingCollection.isPublic" class="share-unpublished">
                            <button 
                                @click="publishCollection" 
                                :disabled="isPublishingCollection"
                            >
                                {{ isPublishingCollection ? $t('aiprompt.editDishModal.publishing') : $t('aiprompt.editDishModal.publishShare') }}
                            </button>
                        </div>
                        
                        <!-- 已發布狀態 - 顯示分享連結和關閉按鈕 -->
                        <div v-else class="share-published">
                            <div class="url-wrap">
                                <div>
                                    <span class="short-url">{{ collectionClipCode }}</span>
                                </div>
                                <span class="copy-btn" @click="copyShareUrl">
                                    <svg-icon icon-class="copy" />
                                </span>
                            </div>
                            <button @click="closeCollectionShare">
                                {{ $t('aiprompt.editDishModal.closeShare') }}
                            </button>
                        </div>
                    </div>
                </div>
                
                <div class="modal-footer">
                    <button @click="closeEditCollectionDialog" class="btn-cancel">
                        {{ $t('aiprompt.cancel') }}
                    </button>
                    <button 
                        @click="updateCollection" 
                        class="btn-confirm"
                        :disabled="!editingCollection.name.trim() || isUpdatingCollection"
                    >
                        {{ isUpdatingCollection ? $t('aiprompt.editDishModal.updating') : $t('aiprompt.editDishModal.confirmUpdate') }}
                    </button>
                </div>
            </div>
        </div>
        
        <!-- 添加指令彈出表單 -->
        <div v-show="showAddPromptForm" class="modal-overlay" @click="closeAddPromptDialog">
            <div class="modal-content add-prompt-modal" @click.stop>
                <div class="modal-header">
                    <h3>{{ $t('aiprompt.addPromptModal.title') }}</h3>
                    <button class="close-btn" @click="closeAddPromptDialog">
                        <svg-icon icon-class="close" />
                    </button>
                </div>
                
                <div class="modal-body">
                    <div class="form-group">
                        <label for="promptCategory">{{ $t('aiprompt.addPromptModal.categoryLabel') }}</label>
                        <select 
                            id="promptCategory"
                            v-model="newPrompt.category"
                            class="form-select"
                        >
                            <option value="">{{ $t('aiprompt.addPromptModal.categoryPlaceholder') }}</option>
                            <option 
                                v-for="option in categoryOptions" 
                                :key="option.value"
                                :value="option.value"
                            >
                                {{ option.label }}
                            </option>
                        </select>
                    </div>
                    
                    <div class="form-group">
                        <label for="promptPurpose">{{ $t('aiprompt.addPromptModal.purposeLabel') }}</label>
                        <input
                            id="promptPurpose"
                            v-model="newPrompt.purpose"
                            type="text"
                            :placeholder="$t('aiprompt.addPromptModal.purposePlaceholder')"
                            class="form-input"
                            maxlength="100"
                        />
                    </div>

                    <div class="form-group">
                        <label for="promptConditionalPrompt">{{ $t('aiprompt.addPromptModal.conditionalPromptLabel') }}</label>
                        <textarea
                            id="promptConditionalPrompt"
                            v-model="newPrompt.conditionalPrompt"
                            :placeholder="$t('aiprompt.addPromptModal.conditionalPromptPlaceholder')"
                            class="form-textarea"
                            rows="4"
                            maxlength="500"
                        ></textarea>
                    </div>

                    <div class="form-group">
                        <label for="promptUsage">{{ $t('aiprompt.addPromptModal.usageLabel') }}</label>
                        <textarea
                            id="promptUsage"
                            v-model="newPrompt.usage"
                            :placeholder="$t('aiprompt.addPromptModal.usagePlaceholder')"
                            class="form-textarea"
                            rows="2"
                            maxlength="200"
                        ></textarea>
                    </div>

                    <div class="form-group">
                        <label for="promptSourceData">{{ $t('aiprompt.addPromptModal.sourceDataLabel') }}</label>
                        <input
                            id="promptSourceData"
                            v-model="newPrompt.sourceData"
                            type="text"
                            :placeholder="$t('aiprompt.addPromptModal.sourceDataPlaceholder')"
                            class="form-input"
                            maxlength="100"
                        />
                    </div>

                    <div class="form-group">
                        <label for="promptSourceDataType">{{ $t('aiprompt.addPromptModal.sourceDataTypeLabel') }}</label>
                        <div class="source-type-toggle">
                            <button
                                type="button"
                                :class="['toggle-btn', { active: newPrompt.sourceDataType === 'text' }]"
                                @click="newPrompt.sourceDataType = 'text'"
                            >
                                {{ $t('aiprompt.addPromptModal.sourceDataTypeText') }}
                            </button>
                            <button
                                type="button"
                                :class="['toggle-btn', { active: newPrompt.sourceDataType === 'image' }]"
                                @click="newPrompt.sourceDataType = 'image'"
                            >
                                {{ $t('aiprompt.addPromptModal.sourceDataTypeImage') }}
                            </button>
                        </div>
                    </div>
                    
                    <div class="form-group">
                        <label for="promptExpectedOutput">{{ $t('aiprompt.addPromptModal.expectedOutputLabel') }}</label>
                        <textarea 
                            id="promptExpectedOutput"
                            v-model="newPrompt.expectedOutput"
                            :placeholder="$t('aiprompt.addPromptModal.expectedOutputPlaceholder')"
                            class="form-textarea"
                            rows="3"
                            maxlength="300"
                        ></textarea>
                    </div>
                    
                    <div class="form-group">
                        <label for="promptFollowUpActions">{{ $t('aiprompt.addPromptModal.followUpActionsLabel') }}</label>
                        <textarea 
                            id="promptFollowUpActions"
                            v-model="newPrompt.followUpActions"
                            :placeholder="$t('aiprompt.addPromptModal.followUpActionsPlaceholder')"
                            class="form-textarea"
                            rows="2"
                            maxlength="200"
                        ></textarea>
                    </div>
                    
                    <div class="form-group">
                        <label for="promptPersonalNotes">{{ $t('aiprompt.addPromptModal.personalNotesLabel') }}</label>
                        <textarea 
                            id="promptPersonalNotes"
                            v-model="newPrompt.personalNotes"
                            :placeholder="$t('aiprompt.addPromptModal.personalNotesPlaceholder')"
                            class="form-textarea"
                            rows="2"
                            maxlength="200"
                        ></textarea>
                    </div>
                </div>
                
                <div class="modal-footer">
                    <button @click="closeAddPromptDialog" class="btn-cancel">
                        {{ $t('aiprompt.cancel') }}
                    </button>
                    <button 
                        @click="createNewPrompt" 
                        class="btn-confirm"
                        :disabled="!isPromptFormValid || isCreatingPrompt"
                    >
                        {{ isCreatingPrompt ? $t('aiprompt.addPromptModal.adding') : $t('aiprompt.addPromptModal.confirmAdd') }}
                    </button>
                </div>
            </div>
        </div>
        
        <!-- 編輯指令彈出表單 -->
        <div v-show="showEditPromptForm" class="modal-overlay" @click="closeEditPromptDialog">
            <div class="modal-content add-prompt-modal" @click.stop>
                <div class="modal-header">
                    <h3>{{ $t('aiprompt.editPromptModal.title') }}</h3>
                    <button class="close-btn" @click="closeEditPromptDialog">
                        <svg-icon icon-class="close" />
                    </button>
                </div>
                
                <div class="modal-body">
                    <div class="form-group">
                        <label for="editPromptCategory">{{ $t('aiprompt.editPromptModal.categoryLabel') }}</label>
                        <select 
                            id="editPromptCategory"
                            v-model="editingPrompt.category"
                            class="form-select"
                        >
                            <option value="">{{ $t('aiprompt.editPromptModal.categoryPlaceholder') }}</option>
                            <option 
                                v-for="option in categoryOptions" 
                                :key="option.value"
                                :value="option.value"
                            >
                                {{ option.label }}
                            </option>
                        </select>
                    </div>
                    
                    <div class="form-group">
                        <label for="editPromptPurpose">{{ $t('aiprompt.editPromptModal.purposeLabel') }}</label>
                        <input
                            id="editPromptPurpose"
                            v-model="editingPrompt.purpose"
                            type="text"
                            :placeholder="$t('aiprompt.editPromptModal.purposePlaceholder')"
                            class="form-input"
                            maxlength="100"
                        />
                    </div>

                    <div class="form-group">
                        <label for="editPromptConditionalPrompt">{{ $t('aiprompt.editPromptModal.conditionalPromptLabel') }}</label>
                        <textarea
                            id="editPromptConditionalPrompt"
                            v-model="editingPrompt.conditionalPrompt"
                            :placeholder="$t('aiprompt.editPromptModal.conditionalPromptPlaceholder')"
                            class="form-textarea"
                            rows="4"
                            maxlength="500"
                        ></textarea>
                    </div>

                    <div class="form-group">
                        <label for="editPromptUsage">{{ $t('aiprompt.editPromptModal.usageLabel') }}</label>
                        <textarea
                            id="editPromptUsage"
                            v-model="editingPrompt.usage"
                            :placeholder="$t('aiprompt.editPromptModal.usagePlaceholder')"
                            class="form-textarea"
                            rows="2"
                            maxlength="200"
                        ></textarea>
                    </div>

                    <div class="form-group">
                        <label for="editPromptSourceData">{{ $t('aiprompt.editPromptModal.sourceDataLabel') }}</label>
                        <input
                            id="editPromptSourceData"
                            v-model="editingPrompt.sourceData"
                            type="text"
                            :placeholder="$t('aiprompt.editPromptModal.sourceDataPlaceholder')"
                            class="form-input"
                            maxlength="100"
                        />
                    </div>

                    <div class="form-group">
                        <label for="editPromptSourceDataType">{{ $t('aiprompt.editPromptModal.sourceDataTypeLabel') }}</label>
                        <div class="source-type-toggle">
                            <button
                                type="button"
                                :class="['toggle-btn', { active: editingPrompt.sourceDataType === 'text' }]"
                                @click="editingPrompt.sourceDataType = 'text'"
                            >
                                {{ $t('aiprompt.editPromptModal.sourceDataTypeText') }}
                            </button>
                            <button
                                type="button"
                                :class="['toggle-btn', { active: editingPrompt.sourceDataType === 'image' }]"
                                @click="editingPrompt.sourceDataType = 'image'"
                            >
                                {{ $t('aiprompt.editPromptModal.sourceDataTypeImage') }}
                            </button>
                        </div>
                    </div>
                    
                    <div class="form-group">
                        <label for="editPromptExpectedOutput">{{ $t('aiprompt.editPromptModal.expectedOutputLabel') }}</label>
                        <textarea 
                            id="editPromptExpectedOutput"
                            v-model="editingPrompt.expectedOutput"
                            :placeholder="$t('aiprompt.editPromptModal.expectedOutputPlaceholder')"
                            class="form-textarea"
                            rows="3"
                            maxlength="300"
                        ></textarea>
                    </div>
                    
                    <div class="form-group">
                        <label for="editPromptFollowUpActions">{{ $t('aiprompt.editPromptModal.followUpActionsLabel') }}</label>
                        <textarea 
                            id="editPromptFollowUpActions"
                            v-model="editingPrompt.followUpActions"
                            :placeholder="$t('aiprompt.editPromptModal.followUpActionsPlaceholder')"
                            class="form-textarea"
                            rows="2"
                            maxlength="200"
                        ></textarea>
                    </div>
                    
                    <div class="form-group">
                        <label for="editPromptPersonalNotes">{{ $t('aiprompt.editPromptModal.personalNotesLabel') }}</label>
                        <textarea 
                            id="editPromptPersonalNotes"
                            v-model="editingPrompt.personalNotes"
                            :placeholder="$t('aiprompt.editPromptModal.personalNotesPlaceholder')"
                            class="form-textarea"
                            rows="2"
                            maxlength="200"
                        ></textarea>
                    </div>
                </div>
                
                <div class="modal-footer">
                    <button @click="closeEditPromptDialog" class="btn-cancel">
                        {{ $t('aiprompt.cancel') }}
                    </button>
                    <button 
                        @click="updatePrompt" 
                        class="btn-confirm"
                        :disabled="!isEditPromptFormValid || isUpdatingPrompt"
                    >
                        {{ isUpdatingPrompt ? $t('aiprompt.editPromptModal.updating') : $t('aiprompt.editPromptModal.confirmUpdate') }}
                    </button>
                </div>
            </div>
        </div>
        
        <!-- 刪除餐盤確認視窗 -->
        <div v-show="showDeleteCollectionConfirm" class="modal-overlay">
            <div class="modal-content delete-confirm-modal">
                <div class="modal-header">
                    <h3>{{ $t('aiprompt.deleteConfirmModal.dishTitle') }}</h3>
                    <button class="close-btn" @click="closeDeleteCollectionConfirm">
                        <svg-icon icon-class="close" />
                    </button>
                </div>
                <div class="modal-body">
                    <div class="confirm-content">
                        <div class="confirm-text">
                            <p>{{ deleteConfirmMessage }}</p>
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="cancel-btn" @click="closeDeleteCollectionConfirm" :disabled="isDeletingItem">
                        {{ $t('aiprompt.cancel') }}
                    </button>
                    <button type="button" class="delete-btn" @click="executeDeleteCollection" :disabled="isDeletingItem">
                        <span v-if="isDeletingItem" class="loading-spinner"></span>
                        {{ isDeletingItem ? $t('aiprompt.deleteConfirmModal.deleting') : $t('aiprompt.deleteConfirmModal.confirmDelete') }}
                    </button>
                </div>
            </div>
        </div>
        
        <!-- 刪除指令確認視窗 -->
        <div v-show="showDeletePromptConfirm" class="modal-overlay">
            <div class="modal-content delete-confirm-modal">
                <div class="modal-header">
                    <h3>{{ $t('aiprompt.deleteConfirmModal.promptTitle') }}</h3>
                    <button class="close-btn" @click="closeDeletePromptConfirm">
                        <svg-icon icon-class="close" />
                    </button>
                </div>
                <div class="modal-body">
                    <div class="confirm-content">
                        <div class="confirm-text">
                            <p>{{ deleteConfirmMessage }}</p>
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="cancel-btn" @click="closeDeletePromptConfirm" :disabled="isDeletingItem">
                        {{ $t('aiprompt.cancel') }}
                    </button>
                    <button type="button" class="delete-btn" @click="executeDeletePrompt" :disabled="isDeletingItem">
                        <span v-if="isDeletingItem" class="loading-spinner"></span>
                        {{ isDeletingItem ? $t('aiprompt.deleteConfirmModal.deleting') : $t('aiprompt.deleteConfirmModal.confirmDelete') }}
                    </button>
                </div>
            </div>
        </div>
        
        <!-- 選擇餐盤分享指令彈窗 -->
        <div v-show="showSelectPlateModal" class="modal-overlay" @click="closeSelectPlateModal">
            <div class="modal-content add-prompt-modal" @click.stop>
                <div class="modal-header">
                    <h3>{{ $t('aiprompt.shareToPlate.title') }}</h3>
                    <button class="close-btn" @click="closeSelectPlateModal">
                        <svg-icon icon-class="close" />
                    </button>
                </div>
                
                <div class="modal-body">
                    <!-- 餐盤選擇下拉選單 -->
                    <div class="form-group">
                        <label class="label-title" for="plateSelect">{{ $t('aiprompt.shareToPlate.selectPlate') }}</label>
                        <select 
                            id="plateSelect"
                            v-model="selectedPlateForShare"
                            class="form-select"
                        >
                            <option :value="null" disabled>{{ $t('aiprompt.shareToPlate.selectPlaceholder') }}</option>
                            <option 
                                v-for="collection in userCollections" 
                                :key="collection.id"
                                :value="collection"
                            >
                                {{ collection.collectionName }}
                            </option>
                        </select>
                    </div>
                    
                    <!-- 分享的指令內容預覽 -->
                    <div class="form-group" v-if="sharePromptData">
                        <label class="label-title">{{ $t('aiprompt.shareToPlate.promptContent') }}</label>
                        <div class="prompt-content-preview">
                            <div class="preview-item" v-if="sharePromptData.name">
                                <div class="item-label">{{ $t('aiprompt.shareToPlate.previewLabels.promptPurpose') }}</div>
                                <div class="item-content">{{ sharePromptData.name }}</div>
                            </div>

                            <div class="preview-item" v-if="sharePromptData.category">
                                <div class="item-label">{{ $t('aiprompt.shareToPlate.previewLabels.promptCategory') }}</div>
                                <div class="item-content">{{ getCategoryLabel(sharePromptData.category) }}</div>
                            </div>

                            <div class="preview-item" v-if="sharePromptData.usage">
                                <div class="item-label">{{ $t('aiprompt.shareToPlate.previewLabels.usage') }}</div>
                                <div class="item-content">{{ sharePromptData.usage }}</div>
                            </div>

                            <div class="preview-item" v-if="sharePromptData.sourceData">
                                <div class="item-label">{{ $t('aiprompt.shareToPlate.previewLabels.sourceData') }}</div>
                                <div class="item-content">{{ sharePromptData.sourceData }}</div>
                            </div>

                            <div class="preview-item">
                                <div class="item-label">{{ $t('aiprompt.shareToPlate.previewLabels.sourceDataType') }}</div>
                                <div class="item-content">{{ (sharePromptData.sourceDataType || 'text') === 'text' ? $t('aiprompt.addPromptModal.sourceDataTypeText') : $t('aiprompt.addPromptModal.sourceDataTypeImage') }}</div>
                            </div>

                            <div class="preview-item" v-if="sharePromptData.conditionalPrompt">
                                <div class="item-label">{{ $t('aiprompt.shareToPlate.previewLabels.conditionalPrompt') }}</div>
                                <div class="item-content">{{ sharePromptData.conditionalPrompt }}</div>
                            </div>

                            <div class="preview-item" v-if="sharePromptData.expectedOutput">
                                <div class="item-label">{{ $t('aiprompt.shareToPlate.previewLabels.expectedOutput') }}</div>
                                <div class="item-content">{{ sharePromptData.expectedOutput }}</div>
                            </div>

                            <div class="preview-item" v-if="sharePromptData.followUpActions">
                                <div class="item-label">{{ $t('aiprompt.shareToPlate.previewLabels.followUpActions') }}</div>
                                <div class="item-content">{{ sharePromptData.followUpActions }}</div>
                            </div>

                            <div class="preview-item" v-if="sharePromptData.personalNotes">
                                <div class="item-label">{{ $t('aiprompt.shareToPlate.previewLabels.personalNotes') }}</div>
                                <div class="item-content">{{ sharePromptData.personalNotes }}</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="modal-footer">
                    <button type="button" class="cancel-btn" @click="closeSelectPlateModal" :disabled="isAddingToPlate">
                        {{ $t('aiprompt.shareToPlate.cancel') }}
                    </button>
                    <button
                        type="button"
                        class="confirm-btn"
                        @click="addPromptToSelectedPlate"
                        :disabled="!selectedPlateForShare || isAddingToPlate"
                    >
                        <span v-if="isAddingToPlate" class="loading-spinner"></span>
                        {{ isAddingToPlate ? $t('aiprompt.shareToPlate.adding') : $t('aiprompt.shareToPlate.addToPlate') }}
                    </button>
                </div>
            </div>
        </div>
    </div>

    <!-- 簡碼搜尋餐盤彈窗 -->
    <div v-if="showShareCodeModal" class="modal-overlay" @click.self="closeShareCodeModal">
        <div class="share-code-modal">
            <div class="modal-header">
                <h2>
                    <svg-icon icon-class="search" class="search-icon" />
                    {{ $t('aiprompt.shareCodeSearch') }}
                </h2>
                <button class="close-btn" @click="closeShareCodeModal">
                    <svg-icon icon-class="close" />
                </button>
            </div>
            <div class="modal-body">
                <!-- 搜尋輸入框 -->
                <div class="search-section">
                    <div class="search-input-wrapper">
                        <div class="input-container">
                            <input
                                v-model="shareCodeInput"
                                type="text"
                                class="share-code-input"
                                :placeholder="$t('aiprompt.shareCodeSearchPlaceholder')"
                                @keyup.enter="searchByShareCode"
                            />
                            <button
                                v-if="shareCodeInput.trim()"
                                class="clear-btn"
                                @click="clearShareCodeInput"
                                title="清除"
                            >
                                <svg-icon icon-class="close" class="clear-icon" />
                            </button>
                        </div>
                        <button
                            class="search-btn"
                            @click="searchByShareCode"
                            :disabled="!shareCodeInput.trim() || isSearchingShareCode"
                        >
                            <span v-if="isSearchingShareCode">{{ $t('aiprompt.loading') }}</span>
                            <span v-else>{{ $t('aiprompt.searchDish') }}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { Message } from 'view-ui-plus'
// import { BlobServiceClient } from '@azure/storage-blob'  // 讀取用 fetch 就夠了
import * as XLSX from 'xlsx'
import api from '@/api'
import aipromptAPI from '@/api/aipromptAPI'
import jwt_decode from 'jwt-decode'
import { useMainStore } from '@/stores/mainStore'
import profileConfig from '@/api/profile'
import SvgIcon from '@/components/SvgIcon.vue'
import AIPromptFavoriteList from './AIPromptFavoriteList.vue'

// 註冊組件（Vue 3 setup語法下會自動註冊導入的組件）

const { t, locale } = useI18n()
const router = useRouter()
const store = useMainStore()

// 取得當前環境配置
const serverLocation = api.main.getServerLocation()
const blobDomainUrl = profileConfig.blobDomain[serverLocation]
const shortURLHost = profileConfig.shortUrlHost[serverLocation]

// 響應式狀態
const isLogin = ref(false)
const showUserMenu = ref(false)
const isAdmin = ref(false)
const isAdminUser = ref(false) // 是否為管理員用戶
const showSidebar = ref(window.innerWidth >= 1200) // 左側邊欄顯示狀態，中螢幕預設隱藏
const profile = reactive({
    name: "",
    teammodelId: "",
    picture: ""
})

// AI Prompt 相關狀態
const isLoadingData = ref(false)
const promptCategories = ref([])
const allPromptData = ref([])
const filteredPromptCategories = ref([])
const selectedFilter = ref('showAll') // 當前篩選條件
const availableFilters = ref([]) // 可用的篩選選項

// 新增餐盤相關狀態
const showAddCollectionForm = ref(false)
const isCreatingCollection = ref(false)
const newCollection = reactive({
    name: '',
    description: '',
    isPublic: true, // 預設為公開
    isFirstTime: false // 是否為首次創建預設餐盤
})

// 用戶餐盤狀態
const userCollections = ref([])
const isLoadingCollections = ref(false)
const selectedCollection = ref(null)
const currentView = ref('official') // 'official', 'custom', 或 'shareCode'

// 簡碼搜尋狀態
const showShareCodeModal = ref(false)
const shareCodeInput = ref('')
const isSearchingShareCode = ref(false)
const shareCodeSearchResult = ref(null)

// 簡碼搜尋頁收藏狀態
const shareCodeIsFavorited = ref(false)
const shareCodeFavoriteId = ref('')
const shareCodeFavoriteLoading = ref(false)

// 收藏餐盤狀態
const selectedFavorite = ref(null)
const favoriteDetailView = ref(false) // 是否顯示收藏餐盤詳情
const favoriteSelectedFilter = ref('showAll') // 收藏餐盤篩選條件
const favoriteFilteredPrompts = ref([]) // 收藏餐盤篩選後的指令

// 自訂餐盤篩選器狀態
const customSelectedFilter = ref('showAll')
const customFilteredPrompts = ref([])

// 編輯餐盤相關狀態
const showEditCollectionForm = ref(false)
const isUpdatingCollection = ref(false)
const editingCollection = reactive({
    id: '',
    name: '',
    description: '',
    isPublic: true,
    isDefault: false
})

// 分享功能相關狀態
const isPublishingCollection = ref(false)
const collectionShareUrl = ref('')
const collectionClipCode = ref('')

// 指令相關狀態
const showAddPromptForm = ref(false)
const isCreatingPrompt = ref(false)
const isLoadingPrompts = ref(false)
const collectionPrompts = ref([])
const newPrompt = reactive({
    category: '',
    purpose: '',
    usage: '',
    sourceData: '',
    sourceDataType: 'text',
    conditionalPrompt: '',
    expectedOutput: '',
    followUpActions: '',
    personalNotes: ''
})

// 分享到餐盤相關狀態
const showSelectPlateModal = ref(false)
const selectedPlateForShare = ref(null)
const sharePromptData = ref(null)
const isAddingToPlate = ref(false)

// 編輯指令相關狀態
const showEditPromptForm = ref(false)
const isUpdatingPrompt = ref(false)
const editingPrompt = reactive({
    id: '',
    category: '',
    purpose: '',
    usage: '',
    sourceData: '',
    sourceDataType: 'text',
    conditionalPrompt: '',
    expectedOutput: '',
    followUpActions: '',
    personalNotes: ''
})

// 儲存原始資料用於變更比較
const originalPromptData = ref({})

// 預覽面板狀態
const selectedPromptForPreview = ref(null)

// 響應式視窗寬度追蹤
const windowWidth = ref(window.innerWidth)

// 判斷是否應該以模態方式顯示預覽面板（中螢幕時）
const shouldShowPreviewAsModal = computed(() => {
    return windowWidth.value < 1200 && selectedPromptForPreview.value !== null
})

// 待刪除項目狀態
const selectedCollectionToDelete = ref(null)
const selectedPromptToDelete = ref(null)
const showDeleteCollectionConfirm = ref(false)
const showDeletePromptConfirm = ref(false)
const deleteConfirmMessage = ref('')
const isDeletingItem = ref(false)

// 指令分類選項（使用計算屬性支援多語系）
const categoryOptions = computed(() => [
    { value: 'score', label: t('aiprompt.categories.score') },
    { value: 'pickup', label: t('aiprompt.categories.pickup') },
    { value: 'general', label: t('aiprompt.categories.general') },
    { value: 'mindMap', label: t('aiprompt.categories.mindMap') },
    { value: 'quiz', label: t('aiprompt.categories.quiz') },
    { value: 'custom', label: t('aiprompt.categories.custom') },
])

// 管理員白名單 (加密或混淆過的 ID)
const ADMIN_WHITELIST = [
    '1595321354',  // 您的用戶 ID
    // '另一個管理員ID'  // 可以加入第二個管理員
]

// 計算屬性
const isPromptFormValid = computed(() => {
    return newPrompt.category.trim() && 
           newPrompt.purpose.trim() && 
           newPrompt.conditionalPrompt.trim()
})

const isEditPromptFormValid = computed(() => {
    return editingPrompt.category.trim() && 
           editingPrompt.purpose.trim() && 
           editingPrompt.conditionalPrompt.trim()
})

// 將官方資料的分類名稱映射為新的英文 category 值
const mapOfficialCategoryToValue = (categoryName) => {
    if (!categoryName) return 'custom'; // 無分類資料時預設為 custom
    
    // 分類映射表（支援三個語系）
    const categoryMap = {
       
        'score': 'score',
        
       
        'general': 'general',
        
       
        'quiz': 'quiz',
        
        
       
        'custom': 'custom',
        
       
        'pickup': 'pickup',
        
        
        'mindMap': 'mindMap',
        'mindmap': 'mindMap',  // 官方文檔有可能是
    };
    
    return categoryMap[categoryName.trim()] || 'custom'; // 找不到對應時預設為 custom
}

// 取得分類中文標籤
const getCategoryLabel = (categoryValue) => {
    const option = categoryOptions.value.find(opt => opt.value === categoryValue)
    return option ? option.label : categoryValue
}

// 獲取官方餐盤的分類標籤
const getOfficialCategoryLabel = () => {
    if (!selectedPromptForPreview.value?.data?.[0]) return ''
    
    const firstItem = selectedPromptForPreview.value.data[0]
    const categoryName = firstItem._filterCategory || 
                        firstItem['Instruction categories'] || 
                        firstItem['指令大類'] || 
                        firstItem['指令大类']
    
    const categoryValue = mapOfficialCategoryToValue(categoryName)
    return getCategoryLabel(categoryValue)
}

// 檢查是否為管理員用戶 (從 id_token 解析)
const checkAdminUser = () => {
    const idToken = localStorage.getItem("id_token")
    
    if (!idToken || !isLogin.value) {
        isAdminUser.value = false
        return
    }
    
    try {
        const decoded = jwt_decode(idToken)
        // 檢查用戶 ID 是否為 1595321354
        isAdminUser.value = decoded.sub === '1595321354'
        
        if (isAdminUser.value) {
            console.log('🔧 檢測到管理員用戶:', decoded.sub)
        }
    } catch (error) {
        console.error('Token 解析錯誤:', error)
        isAdminUser.value = false
    }
}

// 檢查是否為管理員
const checkAdminStatus = () => {
    if (!isLogin.value || !profile.teammodelId) {
        isAdmin.value = false
        return
    }
    
    // 檢查當前用戶是否在管理員白名單中
    isAdmin.value = ADMIN_WHITELIST.includes(profile.teammodelId)
    
    if (isAdmin.value) {
        console.log('🔧 管理員權限已啟用')
        // 存儲管理員狀態（可選）
        sessionStorage.setItem('adminMode', 'true')
    } else {
        sessionStorage.removeItem('adminMode')
    }
}

// 登入方法
const loginTMID = () => {
    api.main.loginTmdID()
}


// 設定使用者認證資訊
const setupUserAuth = (parameters) => {
    const decoded = jwt_decode(parameters.id_Token);
    const profile = {
        name: decoded.name,
        teammodelId: decoded.sub,
        picture: decoded.picture,
    };

    localStorage.setItem("access_token", parameters.access_Token);
    localStorage.setItem("id_token", parameters.id_Token);
    localStorage.setItem("expires_in", parameters.expires_in);
    localStorage.setItem("profile", JSON.stringify(profile));
    sessionStorage.setItem("jumpFrom", "HT");
   
    checkAutoLogin();
}

// 登出方法
const logout = async () => {
    showUserMenu.value = false
    if (isLogin.value) {
        try {
            await api.main.logout()
            Message.info(t('aiprompt.messages.logoutSuccess'))
            isLogin.value = false
            store.setIsLogIn(false)
            
            // 清除本地儲存
            localStorage.removeItem('access_token')
            localStorage.removeItem('id_token')
            localStorage.removeItem('expires_in')
            localStorage.removeItem('profile')
            localStorage.removeItem('auth_token')
            localStorage.removeItem('auth_token_expiry')
            sessionStorage.clear()
            
            // 重置用戶資料
            profile.name = ""
            profile.teammodelId = ""
            profile.picture = ""
            isAdmin.value = false
            sessionStorage.removeItem('adminMode')
        } catch (error) {
            console.error('Logout error:', error)
            Message.error(t('aiprompt.messages.logoutFailed'))
        }
    }
}

// 切換用戶選單
const toggleUserMenu = () => {
    showUserMenu.value = !showUserMenu.value
}

const toggleSidebar = () => {
    showSidebar.value = !showSidebar.value
}

// 窗口大小調整處理
const handleResize = () => {
    // 更新響應式視窗寬度
    windowWidth.value = window.innerWidth
    
    // 如果窗口寬度大於等於 1200px，自動顯示側邊欄
    if (window.innerWidth >= 1200) {
        showSidebar.value = true
    } else {
        // 如果窗口寬度小於 1200px，自動隱藏側邊欄
        showSidebar.value = false
    }
}

// 窗口大小監聽器
const resizeListener = () => {
    handleResize()
}

// 導航到管理員登入頁面
const goToAdminLogin = () => {
    showUserMenu.value = false // 關閉選單
    router.push('/admin-login')
}

// 獲取當前語系
const getCurrentLanguage = () => {
    const currentLocale = locale.value || 'zh_TW'
    
    // 標準化語系代碼
    switch (currentLocale) {
        case 'zh-TW':
        case 'zh_TW':
        case 'tw':
        case 'TW':
            return 'zh_TW'
        case 'zh-CN':
        case 'zh_CN':
        case 'cn':
        case 'CN':
            return 'zh_CN'
        case 'en':
        case 'en-US':
        case 'EN':
            return 'en'
        default:
            return 'zh_TW' // 預設為繁體中文
    }
}

// Excel 解析函數
const parseExcel = (arrayBuffer, currentLanguage = 'zh_TW') => {
    const workbook = XLSX.read(arrayBuffer, { type: 'array' })
    console.log('Excel工作表:', workbook.SheetNames)
    
    // 根據當前語系選擇對應的工作表
    let targetSheetName = null
    
    // 優先查找完全匹配的工作表
    const exactMatch = workbook.SheetNames.find(name => 
        name.includes(currentLanguage)
    )
    
    if (exactMatch) {
        targetSheetName = exactMatch
    } else {
        // 根據語系查找工作表
        if (currentLanguage === 'zh_TW' || currentLanguage === 'zh-TW') {
            targetSheetName = workbook.SheetNames.find(name => 
                name.includes('zh_TW') || name.includes('繁體') || name.includes('TW')
            )
        } else if (currentLanguage === 'zh_CN' || currentLanguage === 'zh-CN') {
            targetSheetName = workbook.SheetNames.find(name => 
                name.includes('zh_CN') || name.includes('簡體') || name.includes('CN')
            )
        } else if (currentLanguage === 'en' || currentLanguage === 'en-US') {
            targetSheetName = workbook.SheetNames.find(name => 
                name.includes('en') || name.includes('英') || name.includes('EN')
            )
        }
    }
    
    // 如果找不到對應語系，使用第一個工作表
    if (!targetSheetName) {
        targetSheetName = workbook.SheetNames[0]
        console.warn(`找不到語系 ${currentLanguage} 的工作表，使用預設工作表: ${targetSheetName}`)
    }
    
    console.log(`使用工作表: ${targetSheetName} (語系: ${currentLanguage})`)
    
    const worksheet = workbook.Sheets[targetSheetName]
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 })
    
    // 轉換為物件格式，第一行作為標題
    const headers = jsonData[0] || []
    const result = []
    
    for (let i = 1; i < jsonData.length; i++) {
        const row = jsonData[i] || []
        if (row.length === 0) continue
        
        const rowObject = {}
        headers.forEach((header, index) => {
            if (header && row[index] !== undefined && row[index] !== '') {
                rowObject[header.toString().trim()] = row[index].toString().trim()
            }
        })
        
        if (Object.keys(rowObject).length > 0) {
            result.push(rowObject)
        }
    }
    
    console.log('Excel解析結果:', result)
    console.log('欄位名稱:', headers)
    
    return result
}

// 從 Azure Blob 讀取 Excel 檔案
const loadAIPromptData = async () => {
    isLoadingData.value = true
    promptCategories.value = []
    allPromptData.value = []
    availableFilters.value = [] // 重置篩選器
    
    try {
        // 先獲取 SAS Token
        const sasResponse = await aipromptAPI.getAIPromptSasToken()
        
        if (!sasResponse.token) {
            throw new Error('無法獲取讀取權限')
        }
        
        // 建構 Blob URL（簡單直接的方式）
        const fileName = 'HT_AIprompt.xlsx'
        const blobUrl = `${blobDomainUrl}/${sasResponse.containerName}/${fileName}?${sasResponse.token}`
        
        // 下載 Excel 檔案
        const response = await fetch(blobUrl)
        if (!response.ok) {
            throw new Error(`檔案讀取失敗: ${response.status}`)
        }
        
        const arrayBuffer = await response.arrayBuffer()
        console.log('Excel 檔案大小:', arrayBuffer.byteLength)
        
        // 獲取當前 i18n 語系
        const currentLocale = getCurrentLanguage()
        console.log('當前語系:', currentLocale)
        
        // 解析 Excel 檔案，根據語系選擇工作表
        const parsedData = parseExcel(arrayBuffer, currentLocale)
        console.log('解析後的 JSON:', parsedData)
        
        allPromptData.value = parsedData
        
        // 從第二欄「指令大類」提取分類（去重）
        const categories = new Set()
        const filterCategories = new Set() // 用於篩選按鈕的分類
        
        parsedData.forEach((row, index) => {
            // 檢查多個可能的欄位名稱，優先使用第二欄
            const categoryName = row['指令用途'] || row['指令大類'] || row['category'] || Object.values(row)[2] || ''
            const filterCategoryName = row['指令大類'] || Object.values(row)[1] || '' // 第二欄作為篩選分類
            
            console.log(`第 ${index + 1} 行資料:`, row)
            console.log(`指令用途:`, categoryName)
            console.log(`指令大類:`, filterCategoryName)
            
            if (categoryName && categoryName.trim() && categoryName !== '') {
                categories.add(categoryName.trim())
            }
            
            if (filterCategoryName && filterCategoryName.trim() && filterCategoryName !== '') {
                filterCategories.add(filterCategoryName.trim())
            }
        })
        
        console.log('所有提取到的指令用途分類:', Array.from(categories))
        console.log('所有提取到的指令大類:', Array.from(filterCategories))
        
        // 設置篩選選項（包含「全部顯示」）
        // 將原始分類名稱轉換為英文 category 值並去重
        const mappedCategories = Array.from(filterCategories)
            .map(categoryName => mapOfficialCategoryToValue(categoryName))
            .filter((value, index, self) => self.indexOf(value) === index) // 去重
        
        // 按照 categoryOptions 的順序排序
        const categoryOrder = ['score', 'pickup', 'general', 'mindMap', 'quiz', 'custom']
        const sortedCategories = mappedCategories.sort((a, b) => {
            const indexA = categoryOrder.indexOf(a)
            const indexB = categoryOrder.indexOf(b)
            return indexA - indexB
        })
        
        availableFilters.value = ['showAll', ...sortedCategories]
        
        // 轉換為按鈕資料
        promptCategories.value = Array.from(categories).map((category, index) => {
            const categoryData = parsedData.filter(row => {
                const rowCategory = row['指令用途'] || row['指令大類'] || row['category'] || Object.values(row)[2] || ''
                return rowCategory && rowCategory.trim() === category
            })
            
            // 為每個項目添加大類信息
            categoryData.forEach(item => {
                item._filterCategory = item['指令大類'] || Object.values(item)[1] || ''
            })
            
            console.log(`分類「${category}」包含 ${categoryData.length} 個項目`)
            
            return {
                id: index + 1,
                name: category,
                data: categoryData,
                filterCategory: mapOfficialCategoryToValue(categoryData[0]?._filterCategory || '') // 該分類所屬的大類（轉為英文值）
            }
        })
        
        // 初始化篩選結果為全部
        filteredPromptCategories.value = promptCategories.value
        selectedFilter.value = 'showAll'
        
        console.log('分類按鈕資料:', promptCategories.value)
        
        if (promptCategories.value.length > 0) {
            Message.success(t('aiprompt.messages.loadSuccess', { count: promptCategories.value.length }))
        } else {
            Message.warning(t('aiprompt.messages.noValidCategoryData'))
        }
        
    } catch (error) {
        console.error('載入 AI Prompt 資料失敗:', error)
        Message.error(t('aiprompt.messages.loadFailed', { error: error.message || t('aiprompt.messages.cannotReadFile') }))
        
        // 即使載入失敗，也要設置基本的篩選器
        availableFilters.value = ['showAll']
    } finally {
        isLoadingData.value = false
    }
}

// 篩選功能
const applyFilter = (filterName) => {
    selectedFilter.value = filterName
    
    if (filterName === 'showAll') {
        filteredPromptCategories.value = promptCategories.value
    } else {
        // 篩選出符合指定大類的分類
        filteredPromptCategories.value = promptCategories.value.filter(category => 
            category.filterCategory === filterName
        )
    }
    
    console.log(`篩選器已應用: ${filterName}`)
    console.log(`顯示 ${filteredPromptCategories.value.length} 個分類`)
}

// 獲取篩選器數量
const getFilterCount = (filterName) => {
    if (filterName === 'showAll') {
        return promptCategories.value.length
    }
    return promptCategories.value.filter(category => 
        category.filterCategory === filterName
    ).length
}

// 自訂餐盤篩選邏輯
const applyCustomFilter = (filterName) => {
    customSelectedFilter.value = filterName
    
    if (filterName === 'showAll') {
        customFilteredPrompts.value = collectionPrompts.value
    } else {
        customFilteredPrompts.value = collectionPrompts.value.filter(prompt => 
            prompt.category === filterName
        )
    }
    
    console.log(`自訂餐盤篩選器已應用: ${filterName}`)
    console.log(`顯示 ${customFilteredPrompts.value.length} 個指令`)
}

// 獲取自訂餐盤篩選器數量
const getCustomFilterCount = (filterName) => {
    if (filterName === 'showAll') {
        return collectionPrompts.value.length
    }
    return collectionPrompts.value.filter(prompt =>
        prompt.category === filterName
    ).length
}

// 收藏餐盤篩選邏輯
const applyFavoriteFilter = (filterName) => {
    favoriteSelectedFilter.value = filterName

    if (!selectedFavorite.value || !selectedFavorite.value.prompts) {
        favoriteFilteredPrompts.value = []
        return
    }

    if (filterName === 'showAll') {
        favoriteFilteredPrompts.value = selectedFavorite.value.prompts
    } else {
        favoriteFilteredPrompts.value = selectedFavorite.value.prompts.filter(prompt =>
            prompt.category === filterName
        )
    }

    console.log(`收藏餐盤篩選器已應用: ${filterName}`)
    console.log(`顯示 ${favoriteFilteredPrompts.value.length} 個指令`)
}

// 獲取收藏餐盤篩選器數量
const getFavoriteFilterCount = (filterName) => {
    if (!selectedFavorite.value || !selectedFavorite.value.prompts) {
        return 0
    }

    if (filterName === 'showAll') {
        return selectedFavorite.value.prompts.length
    }
    return selectedFavorite.value.prompts.filter(prompt =>
        prompt.category === filterName
    ).length
}

// 顯示分類詳細資料（官方餐盤預覽）
const showPromptDetails = (category) => {
    selectedPromptForPreview.value = category
    console.log(`顯示「${category.name}」的詳細資料預覽`)
}

// 關閉預覽面板
const closePreview = () => {
    selectedPromptForPreview.value = null
}


// 過濾項目欄位，移除 _filterCategory、指令大類和各語言版本的編號
const getFilteredItemEntries = (item) => {
    // 定義需要過濾的關鍵字模式
    const filterPatterns = [
        '_filterCategory',  // 內部分類標記
        '指令大類',  // 指令大類
        '編號',  // 繁體中文
        '编号',  // 簡體中文
        'id',  // 英文可能使用
        'number',  // 英文可能使用
        'no',  // 英文可能使用
        'serial',  // 英文可能使用
        'seq',  // 英文可能使用
        'sequence'  // 英文可能使用
    ];
    
    // 使用不區分大小寫的比較來過濾欄位
    return Object.entries(item).filter(([key]) => {
        // 轉為小寫進行比較
        const lowerKey = key.toLowerCase();
        // 檢查是否包含任何需要過濾的關鍵字
        return !filterPatterns.some(pattern => lowerKey.includes(pattern.toLowerCase()));
    });
}

// 從預覽面板編輯指令
const editPromptFromPreview = () => {
    if (selectedPromptForPreview.value && currentView.value === 'custom') {
        editPrompt(selectedPromptForPreview.value)
    }
}

// 顯示自訂餐盤指令預覽
const showCustomPromptPreview = (prompt) => {
    selectedPromptForPreview.value = prompt
    console.log(`顯示自訂指令「${prompt.purpose}」的預覽`)
}

// 顯示收藏餐盤指令預覽
const showFavoritePromptPreview = (prompt) => {
    // 設定預覽資料，包含餐盤資訊
    selectedPromptForPreview.value = {
        ...prompt,
        collectionId: selectedFavorite.value.CollectionId,
        collectionName: selectedFavorite.value.collection?.CollectionName || selectedFavorite.value.CollectionName
    }
    console.log(`顯示收藏指令「${prompt.purpose}」的預覽`)
}

// 傳送指令內容到 HT (暫時在 console 印出 JSON)
const copyPromptContent = async () => {
    if (!selectedPromptForPreview.value) return
    
    try {
        let jsonData = {}
        
        if (currentView.value === 'official') {
            // 官方指令 - 轉換為統一的 custom 格式
            const officialDataArray = JSON.parse(JSON.stringify(selectedPromptForPreview.value.data))
            const firstItem = officialDataArray[0] || {}  // 取得第一個項目的資料

            // Debug: 查看實際的欄位名稱 (開發用)
            console.log('=== 官方指令原始資料 ===')
            console.log('firstItem keys:', Object.keys(firstItem))
            console.log('firstItem:', firstItem)
            
            // 從官方指令的欄位名稱對應到統一格式 (支援中英文欄位：繁體/簡體/英文)
            jsonData = {
                type: 'custom',
                name: firstItem['Instruction purpose'] || firstItem['指令用途'] || firstItem['指令用途'] || selectedPromptForPreview.value.name,
                category: mapOfficialCategoryToValue(firstItem._filterCategory || firstItem['Instruction categories'] || firstItem['指令大類'] || firstItem['指令大类']),
                categoryLabel: getCategoryLabel(mapOfficialCategoryToValue(firstItem._filterCategory || firstItem['Instruction categories'] || firstItem['指令大類'] || firstItem['指令大类'])) || null,
                usage: firstItem['How to use'] || firstItem['用法'] || firstItem['使用方式'] || null,
                conditionalPrompt: firstItem['Conditional instruction content'] || firstItem['條件指令內容'] || firstItem['条件指令内容'] || firstItem['條件式指令'] || null,
                expectedOutput: firstItem['AI Output'] || firstItem['AI輸出'] || firstItem['AI输出'] || firstItem['預期輸出'] || null,
                followUpActions: firstItem['Follow-up actions'] || firstItem['後續動作'] || firstItem['后续动作'] || firstItem['後續行動'] || null,
                personalNotes: null,
                sourceMaterial: firstItem['Source material'] || firstItem['來源資料'] || firstItem['来源资料'] || null,
                sourceDataType: firstItem['Source data type'] || firstItem['來源資料類型'] || firstItem['来源数据类型'] || 'text',
                creator: firstItem['創建者'] || firstItem['Creator'] || null,
                collectionId: null,
                collectionName: '官方指令集'
            }
        } else if (currentView.value === 'custom') {
            // 自訂餐盤內容
            jsonData = {
                type: 'custom',
                name: selectedPromptForPreview.value.purpose,
                category: selectedPromptForPreview.value.category,
                categoryLabel: getCategoryLabel(selectedPromptForPreview.value.category),
                usage: selectedPromptForPreview.value.usage || null,
                conditionalPrompt: selectedPromptForPreview.value.conditionalPrompt,
                expectedOutput: selectedPromptForPreview.value.expectedOutput || null,
                followUpActions: selectedPromptForPreview.value.followUpActions || null,
                personalNotes: selectedPromptForPreview.value.personalNotes || null,
                sourceMaterial: selectedPromptForPreview.value.sourceData || null,
                sourceDataType: selectedPromptForPreview.value.sourceDataType || 'text',
                creator: selectedPromptForPreview.value.creator || null,
                collectionId: selectedCollection.value?.id || null,
                collectionName: selectedCollection.value?.collectionName || null
            }
        } else if (currentView.value === 'favorites') {
            // 收藏餐盤內容
            jsonData = {
                type: 'custom',
                name: selectedPromptForPreview.value.purpose,
                category: selectedPromptForPreview.value.category,
                categoryLabel: getCategoryLabel(selectedPromptForPreview.value.category),
                usage: selectedPromptForPreview.value.usage || null,
                conditionalPrompt: selectedPromptForPreview.value.conditionalPrompt,
                expectedOutput: selectedPromptForPreview.value.expectedOutput || null,
                followUpActions: selectedPromptForPreview.value.followUpActions || null,
                personalNotes: selectedPromptForPreview.value.personalNotes || null,
                sourceMaterial: selectedPromptForPreview.value.sourceData || null,
                sourceDataType: selectedPromptForPreview.value.sourceDataType || 'text',
                creator: selectedPromptForPreview.value.creator || null,
                collectionId: selectedFavorite.value?.collection?.id || selectedFavorite.value?.CollectionId || null,
                collectionName: selectedFavorite.value?.collection?.collectionName || selectedFavorite.value?.CollectionName || null
            }
        } else if (currentView.value === 'shareCode') {
            // 簡碼搜尋餐盤內容
            jsonData = {
                type: 'custom',
                name: selectedPromptForPreview.value.purpose,
                category: selectedPromptForPreview.value.category,
                categoryLabel: getCategoryLabel(selectedPromptForPreview.value.category),
                usage: selectedPromptForPreview.value.usage || null,
                conditionalPrompt: selectedPromptForPreview.value.conditionalPrompt,
                expectedOutput: selectedPromptForPreview.value.expectedOutput || null,
                followUpActions: selectedPromptForPreview.value.followUpActions || null,
                personalNotes: selectedPromptForPreview.value.personalNotes || null,
                sourceMaterial: selectedPromptForPreview.value.sourceData || null,
                sourceDataType: selectedPromptForPreview.value.sourceDataType || 'text',
                creator: selectedPromptForPreview.value.creator || null,
                collectionId: shareCodeSearchResult.value?.id || null,
                collectionName: shareCodeSearchResult.value?.name || null
            }
        }
        
        // Debug: 在 console 印出格式化的 JSON
        console.log('=== 傳送給 HT 的指令資料 ===')
        console.log(JSON.stringify(jsonData, null, 2))
        
        // 使用通訊協議發送到 HT (替代原本的 console 印出)
        if (isWebView.value) {
            sendAIPromptToHT(jsonData)
            // 顯示成功訊息
            Message.success(t('aiprompt.messages.sentToHT'))
        } else {
            // 非 WebView 環境，仍然只在 console 印出
            Message.info(t('aiprompt.messages.outputToConsole'))
        }
        
    } catch (error) {
        console.error('傳送指令資料失敗:', error)
        Message.error(t('aiprompt.messages.sendFailed'))
    }
}

// 顯示新增餐盤對話框
const showAddCollectionDialog = async () => {
    if (!isLogin.value) {
        Message.warning(t('aiprompt.messages.pleaseLoginToAddDish'))
        return
    }
    
    // 檢查是否為首次創建
    const hasCollections = await checkUserHasCollections()
    
    // 重置表單資料
    newCollection.name = ''
    newCollection.description = ''
    newCollection.isPublic = true // 預設為公開
    newCollection.isFirstTime = !hasCollections // 首次創建時為 true
    
    showAddCollectionForm.value = true
}

// 關閉新增餐盤對話框
const closeAddCollectionDialog = () => {
    showAddCollectionForm.value = false
    isCreatingCollection.value = false
    
    // 重置表單資料
    newCollection.name = ''
    newCollection.description = ''
    newCollection.isPublic = true // 預設為公開
    newCollection.isFirstTime = false
}

// 檢查用戶是否已有餐盤
const checkUserHasCollections = async () => {
    try {
        const response = await aipromptAPI.getCollectionsByCreator({
            creatorId: profile.teammodelId
        })
        return response && response.data && response.data.length > 0
    } catch (error) {
        console.log('檢查用戶餐盤失敗，假設為首次創建:', error)
        return false
    }
}

// 創建餐盤
const createCollection = async () => {
    // 只有非首次創建才需要檢查名稱
    if (!newCollection.isFirstTime && !newCollection.name.trim()) {
        Message.warning(t('aiprompt.messages.pleaseEnterDishName'))
        return
    }
    
    if (!isLogin.value || !profile.teammodelId) {
        Message.error(t('aiprompt.messages.pleaseLogin'))
        return
    }
    
    isCreatingCollection.value = true
    
    try {
        // 檢查是否為首次創建
        const hasCollections = await checkUserHasCollections()
        
        const collectionData = {
            creator: {
                id: profile.teammodelId,
                name: profile.name || '匿名用戶'
            },
            collectionName: !hasCollections ? t('aiprompt.defaultDishName') : newCollection.name.trim(),
            description: newCollection.description.trim() || ''
        }
        
        console.log('創建餐盤資料:', collectionData)
        console.log('是否為首次創建:', !hasCollections)
        
        let response
        if (!hasCollections) {
            // 首次創建使用 create-default
            response = await aipromptAPI.createDefaultCollection(collectionData)
        } else {
            // 已有餐盤，使用一般創建
            response = await aipromptAPI.createCollection(collectionData)
        }
        
        // 檢查回應格式：成功會直接回傳餐盤物件，包含 id 欄位
        if (response && response.id) {
            Message.success(t('aiprompt.messages.dishCreated'))
            closeAddCollectionDialog()

            // 重新載入用戶餐盤列表
            await loadUserCollections()

            // 自動選中剛創建的餐盤，使用完整的選中邏輯
            await selectCustomCollection(response)
            console.log('新建餐盤回應:', response)
            console.log('已自動選中新創建的餐盤:', response.collectionName || response.name)
        } else {
            throw new Error(response?.message || '創建餐盤失敗')
        }
        
    } catch (error) {
        console.error('創建餐盤錯誤:', error)
        Message.error(t('aiprompt.messages.createDishFailed', { error: error.message || t('aiprompt.errors.unknownError') }))
    } finally {
        isCreatingCollection.value = false
    }
}

// 載入用戶餐盤
const loadUserCollections = async () => {
    if (!isLogin.value || !profile.teammodelId) {
        userCollections.value = []
        return
    }
    
    isLoadingCollections.value = true
    
    try {
        const response = await aipromptAPI.getCollectionsByCreator({
            creatorId: profile.teammodelId
        })
        
        if (response && response.data) {
            // 排序餐盤：預設餐盤永遠在最上面
            const sortedCollections = response.data.sort((a, b) => {
                // 檢查是否為預設餐盤
                const aIsDefault = a.tags && a.tags.includes('default')
                const bIsDefault = b.tags && b.tags.includes('default')
                
                // 預設餐盤優先
                if (aIsDefault && !bIsDefault) return -1
                if (!aIsDefault && bIsDefault) return 1
                
                // 都是預設餐盤或都不是預設餐盤時，保持原順序
                return 0
            })
            
            userCollections.value = sortedCollections
            console.log('載入用戶餐盤成功:', userCollections.value)

            // 自動選擇預設餐盤，如果沒有就選第一個
            const defaultPlate = sortedCollections.find(collection =>
                collection.tags && collection.tags.includes('default')
            )
            if (defaultPlate) {
                selectedPlateForShare.value = defaultPlate
                console.log('已自動選擇預設餐盤:', defaultPlate.collectionName)
            } else if (sortedCollections.length > 0) {
                selectedPlateForShare.value = sortedCollections[0]
                console.log('已自動選擇第一個餐盤:', sortedCollections[0].collectionName)
            }
        } else {
            userCollections.value = []
        }
        
    } catch (error) {
        console.error('載入用戶餐盤失敗:', error)
        userCollections.value = []
    } finally {
        isLoadingCollections.value = false
    }
}

// 切換到官方視圖
const switchToOfficialView = () => {
    currentView.value = 'official'
    selectedCollection.value = null
    // 關閉預覽面板
    selectedPromptForPreview.value = null
}

// 切換到收藏視圖
const switchToFavoritesView = () => {
    currentView.value = 'favorites'
    selectedCollection.value = null
    selectedFavorite.value = null
    favoriteDetailView.value = false
    // 關閉預覽面板
    selectedPromptForPreview.value = null
}

// 選擇收藏餐盤並顯示詳情
const selectFavoriteCollection = async (favorite) => {
    try {
        // console.log('=== 選擇收藏餐盤 ===')
        // console.log('傳入的 favorite:', favorite)

        // 查詢收藏餐盤的完整詳情
        const response = await aipromptAPI.getFavoriteDetail({
            favoriteId: favorite.id
        })

        // console.log('API 回應:', response)
        // console.log('response.collection 完整內容:', response?.collection ?? null)
        // console.log('response.collection.CollectionName:', response?.collection?.CollectionName)
        // console.log('response.collection.collectionName:', response?.collection?.collectionName)
        // console.log('response.collection.Creator:', response?.collection?.Creator)
        // console.log('response.collection.creator:', response?.collection?.creator)
        // console.log('response.prompts 數量:', response?.prompts?.length)
        // console.log('第一個 prompt:', response?.prompts?.[0])

        if (response && response.isValid) {
            // 餐盤有效，設定選中的收藏和顯示詳情
            selectedFavorite.value = {
                ...favorite,
                collection: response.collection,
                prompts: response.prompts
            }

            // console.log('設定後的 selectedFavorite:', JSON.parse(JSON.stringify(selectedFavorite.value)))
            // console.log('餐盤名稱:', selectedFavorite.value.collection?.CollectionName)
            // console.log('創建者:', selectedFavorite.value.collection?.Creator)
            // console.log('所有 collection 欄位:', Object.keys(response?.collection || {}))

            favoriteDetailView.value = true

            // 初始化篩選狀態
            favoriteSelectedFilter.value = 'showAll'
            favoriteFilteredPrompts.value = response.prompts || []

            // console.log('篩選後的指令:', favoriteFilteredPrompts.value)
        } else {
            // 餐盤無效（已刪除或不公開）
            // console.log('餐盤無效或不公開')
            const unavailableMessage = t('aiprompt.messages.collectionUnavailable')
            Message.error(unavailableMessage)
        }
    } catch (error) {
        console.error('載入收藏詳情失敗:', error)
        const unavailableMessage = t('aiprompt.messages.collectionUnavailable')
        Message.error(unavailableMessage)
    }
}

// 從收藏餐盤詳情返回收藏列表
const backToFavoritesList = () => {
    favoriteDetailView.value = false
    selectedFavorite.value = null
    // 關閉預覽面板
    selectedPromptForPreview.value = null
}

// 關閉簡碼搜尋彈窗
const closeShareCodeModal = () => {
    showShareCodeModal.value = false
    // 清空搜尋輸入
    shareCodeInput.value = ''
    // 清空之前的搜尋結果
    shareCodeSearchResult.value = null
}

// 透過分享代碼搜尋餐盤
// 清除分享代碼輸入框
const clearShareCodeInput = () => {
    shareCodeInput.value = ''
    shareCodeSearchResult.value = null
}

const searchByShareCode = async () => {
    if (!shareCodeInput.value.trim()) {
        return
    }

    isSearchingShareCode.value = true
    shareCodeSearchResult.value = null

    try {
        console.log('搜尋分享代碼:', shareCodeInput.value.trim())

        const response = await aipromptAPI.getCollectionByShareCode({
            shareCode: shareCodeInput.value.trim()
        })

        console.log('分享代碼搜尋結果:', response)

        if (response && response.data) {
            console.log('原始餐盤資料:', response.data.collection)
            console.log('餐盤ID:', response.data.collection?.id)
            console.log('餐盤名稱(CollectionName):', response.data.collection?.CollectionName)
            console.log('餐盤名稱(collectionName):', response.data.collection?.collectionName)

            // 重新組織資料結構以符合預期格式
            const searchResult = {
                id: response.data.collection?.id || '',
                name: response.data.collection?.CollectionName || response.data.collection?.collectionName || '未知餐盤',
                description: response.data.collection?.Description || response.data.collection?.description || '',
                prompts: response.data.prompts || [],
                creator: response.data.collection?.Creator || response.data.collection?.creator || null
            }

            console.log('重組後的搜尋結果:', searchResult)

            // 檢查收藏狀態
            shareCodeSearchResult.value = searchResult
            await checkShareCodeFavoriteStatus()

            // 自動加入收藏
            if (isLogin.value && !shareCodeIsFavorited.value) {
                await autoAddShareCodeFavorite()
            }

            // 關閉彈窗
            showShareCodeModal.value = false
            shareCodeInput.value = ''

            // 切換到收藏餐盤視圖並顯示該餐盤詳情
            currentView.value = 'favorites'
            selectedCollection.value = null

            // 設定選中的收藏餐盤詳情
            selectedFavorite.value = {
                collection: {
                    id: searchResult.id,
                    collectionName: searchResult.name,
                    description: searchResult.description,
                    creator: searchResult.creator
                },
                prompts: searchResult.prompts
            }
            favoriteDetailView.value = true

            // 初始化篩選狀態
            favoriteSelectedFilter.value = 'showAll'
            favoriteFilteredPrompts.value = searchResult.prompts || []

            // 清空搜尋結果（已跳轉，不需要保留）
            shareCodeSearchResult.value = null

        } else {
            Message.error(t('aiprompt.errors.shareCodeNotFound'))
        }
    } catch (error) {
        console.error('分享代碼搜尋失敗:', error)
        Message.error(t('aiprompt.errors.shareCodeSearchFailed'))
        shareCodeSearchResult.value = null
    } finally {
        isSearchingShareCode.value = false
    }
}

// 檢查簡碼搜尋頁收藏狀態
const checkShareCodeFavoriteStatus = async () => {
    if (!isLogin.value || !shareCodeSearchResult.value?.id) {
        return
    }

    try {
        const idToken = localStorage.getItem('id_token')
        if (!idToken) return

        const decoded = jwt_decode(idToken)
        const userId = decoded.sub

        const response = await aipromptAPI.checkIsFavorite({
            userId: userId,
            collectionId: shareCodeSearchResult.value.id
        })

        if (response && response.success) {
            shareCodeIsFavorited.value = response.isFavorited
            if (response.favorite) {
                shareCodeFavoriteId.value = response.favorite.id
            }
        }
    } catch (error) {
        console.error('檢查收藏狀態失敗:', error)
    }
}

// 自動加入簡碼搜尋頁收藏
const autoAddShareCodeFavorite = async () => {
    console.log('=== 開始自動加入收藏 ===')
    console.log('isLogin:', isLogin.value)
    console.log('shareCodeSearchResult.value?.id:', shareCodeSearchResult.value?.id)
    console.log('shareCodeIsFavorited:', shareCodeIsFavorited.value)

    if (!isLogin.value || !shareCodeSearchResult.value?.id || shareCodeIsFavorited.value) {
        console.log('跳過自動收藏：條件不符合')
        return
    }

    try {
        const idToken = localStorage.getItem('id_token')
        if (!idToken) {
            console.log('沒有 id_token')
            return
        }

        const decoded = jwt_decode(idToken)
        const userId = decoded.sub

        console.log('發送加入收藏請求:', {
            userId,
            collectionId: shareCodeSearchResult.value.id
        })

        shareCodeFavoriteLoading.value = true

        const response = await aipromptAPI.addFavorite({
            userId: userId,
            collectionId: shareCodeSearchResult.value.id
        })

        console.log('加入收藏 API 回應:', response)

        if (response && response.success) {
            shareCodeIsFavorited.value = true
            if (response.favorite) {
                shareCodeFavoriteId.value = response.favorite.id
                console.log('收藏成功，收藏資料:', response.favorite)
            }
            Message.success(t('aiprompt.messages.autoFavorited'))
            console.log('自動加入收藏成功')
        } else {
            console.log('加入收藏失敗：', response)
        }
    } catch (error) {
        console.error('自動加入收藏失敗:', error)
    } finally {
        shareCodeFavoriteLoading.value = false
    }
}

// 選擇自訂餐盤
const selectCustomCollection = async (collection) => {
    selectedCollection.value = collection
    currentView.value = 'custom'
    console.log('選擇餐盤:', collection)
    
    // 關閉預覽面板
    selectedPromptForPreview.value = null
    
    // 載入該餐盤的指令
    if (collection && collection.id) {
        await loadCollectionPrompts(collection.id)
    }
}

// 顯示編輯餐盤對話框
const showEditCollectionDialog = (collection) => {
    editingCollection.id = collection.id
    editingCollection.name = collection.collectionName || collection.name || ''
    editingCollection.description = collection.description || ''
    editingCollection.isPublic = collection.isPublic !== false
    // 檢查是否為預設餐盤（包含 'default' 標籤）
    editingCollection.isDefault = collection.tags && collection.tags.includes('default')
    
    // 設置分享相關狀態
    collectionClipCode.value = collection.shareCode || collection.clipCode || ''
    if (editingCollection.isPublic && collectionClipCode.value) {
        collectionShareUrl.value = shortURLHost + collectionClipCode.value
    } else {
        collectionShareUrl.value = ''
    }
    
    showEditCollectionForm.value = true
}

// 關閉編輯餐盤對話框
const closeEditCollectionDialog = () => {
    showEditCollectionForm.value = false
    isUpdatingCollection.value = false
    
    // 重置編輯表單
    editingCollection.id = ''
    editingCollection.name = ''
    editingCollection.description = ''
    editingCollection.isPublic = true
    editingCollection.isDefault = false
    
    // 重置分享狀態
    collectionShareUrl.value = ''
    collectionClipCode.value = ''
    isPublishingCollection.value = false
}

// 發布餐盤共用
const publishCollection = async () => {
    if (!editingCollection.id) {
        Message.error(t('aiprompt.messages.dishIdError'))
        return
    }
    
    isPublishingCollection.value = true
    
    try {
        // 生成減碼
        const origData = `${window.location.origin}/aiprompt-collect?id=${editingCollection.id}`
        const clipCodeResponse = await api.mindmapAPI.getClipCode({
            origDataList: [{ origData: origData }],
            type: "url",
            useShortDigits: false,
            isPermanent: false,
            lang: locale.value.toLowerCase(),
            note: JSON.stringify({collectionName: editingCollection.name})
        })
        
        if (clipCodeResponse && clipCodeResponse.clipCodePairs && clipCodeResponse.clipCodePairs[0]) {
            collectionClipCode.value = clipCodeResponse.clipCodePairs[0].clipCode

            console.log('生成減碼成功:', collectionClipCode.value)
            
            // 更新餐盤為公開狀態
            const updateData = {
                cid: editingCollection.id,
                editor: {
                    id: profile.teammodelId,
                    name: profile.name 
                },
                collectionName: editingCollection.name.trim(),
                description: editingCollection.description.trim() || '',
                isPublic: true,
                shareCode: collectionClipCode.value
            }
            
            const response = await aipromptAPI.updateCollection(updateData)
            
            if (response && response.existingCollection) {
                editingCollection.isPublic = true
                collectionShareUrl.value = shortURLHost + collectionClipCode.value
                
                Message.success(t('aiprompt.messages.dishPublished'))
                
                // 重新載入用戶餐盤列表
                await loadUserCollections()
            } else {
                throw new Error('更新餐盤狀態失敗')
            }
        } else {
            throw new Error('生成減碼失敗')
        }
    } catch (error) {
        console.error('發布餐盤共用失敗:', error)
        Message.error(t('aiprompt.messages.publishFailed', { error: error.message || '未知錯誤' }))
    } finally {
        isPublishingCollection.value = false
    }
}

// 關閉餐盤共用
const closeCollectionShare = async () => {
    if (!editingCollection.id) {
        Message.error(t('aiprompt.messages.dishIdError'))
        return
    }
    
    try {
        // 更新餐盤為非公開狀態
        const updateData = {
            cid: editingCollection.id,
            editor: {
                id: profile.teammodelId,
                name: profile.name || '匿名用戶'
            },
            collectionName: editingCollection.name.trim(),
            description: editingCollection.description.trim() || '',
            isPublic: false,
            shareCode: null // 移除減碼
        }
        
        const response = await aipromptAPI.updateCollection(updateData)
        
        if (response && response.existingCollection) {
            editingCollection.isPublic = false
            collectionShareUrl.value = ''
            collectionClipCode.value = ''
            
            Message.success(t('aiprompt.messages.dishShareClosed'))
            
            // 重新載入用戶餐盤列表
            await loadUserCollections()
        } else {
            throw new Error('更新餐盤狀態失敗')
        }
    } catch (error) {
        console.error('關閉餐盤共用失敗:', error)
        Message.error(t('aiprompt.messages.closeShareFailed', { error: error.message || '未知錯誤' }))
    }
}

// 複製分享連結
const copyShareUrl = async () => {
    const fullUrl = collectionClipCode.value
    if (!fullUrl || !collectionClipCode.value) {
        Message.warning(t('aiprompt.messages.noShareLinkToCopy'))
        return
    }
    
    try {
        // 使用傳統方式複製，與 MindMap 保持一致
        const input = document.createElement('input')
        input.value = fullUrl
        document.body.appendChild(input)
        input.select()
        document.execCommand('Copy')
        document.body.removeChild(input)
        
        Message.success(t('aiprompt.messages.shareLinkCopied'))
    } catch (error) {
        console.error('複製失敗:', error)
        Message.error(t('aiprompt.messages.copyLinkFailed'))
    }
}

// 更新餐盤
const updateCollection = async () => {
    if (!editingCollection.name.trim()) {
        Message.warning(t('aiprompt.messages.pleaseEnterDishName'))
        return
    }
    
    if (!editingCollection.id) {
        Message.error(t('aiprompt.messages.dishIdError'))
        return
    }
    
    isUpdatingCollection.value = true
    
    try {
        const updateData = {
            cid: editingCollection.id,
            editor: {
                id: profile.teammodelId,
                name: profile.name || '匿名用戶'
            },
            collectionName: editingCollection.name.trim(),
            description: editingCollection.description.trim() || ''
        }
        
        console.log('更新餐盤資料:', updateData)
        
        const response = await aipromptAPI.updateCollection(updateData)
        
        // 檢查回應：如果有 existingCollection.id 表示更新成功
        if (response && response.existingCollection && response.existingCollection.id) {
            Message.success(t('aiprompt.messages.dishUpdated'))
            
            // 先記住編輯的餐盤ID，再關閉對話框
            const editedCollectionId = editingCollection.id
            closeEditCollectionDialog()
            
            // 如果當前選中的餐盤被編輯，立即更新選中的餐盤
            if (selectedCollection.value?.id === editedCollectionId) {
                
                // 直接更新相關欄位，確保響應式更新
                selectedCollection.value.collectionName = response.existingCollection.collectionName
                selectedCollection.value.description = response.existingCollection.description
                selectedCollection.value.updateTime = response.existingCollection.updateTime
                
            } 
            
            // 重新載入餐盤列表
            await loadUserCollections()
            
            console.log('餐盤更新回應:', response)
        } else {
            throw new Error(response?.message || '更新餐盤失敗')
        }
        
    } catch (error) {
        console.error('更新餐盤錯誤:', error)
        Message.error(t('aiprompt.messages.updateDishFailed', { error: error.message || t('aiprompt.errors.unknownError') }))
    } finally {
        isUpdatingCollection.value = false
    }
}

// 刪除餐盤
const deleteCollection = async (collection) => {
    const collectionName = collection.collectionName || collection.name
    
    try {
        // 1. 先檢查餐盤內的指令數量
        console.log('檢查餐盤內指令數量:', collection.id)
        
        const countResponse = await aipromptAPI.getCollectionPromptCount({
            collectionId: collection.id
        })
        
        console.log('指令數量回應:', countResponse)
        
        let promptCount = 0
        if (countResponse && countResponse.data) {
            promptCount = countResponse.data.count || 0
        }
        
        // 2. 根據指令數量顯示不同的確認對話框
        let confirmMessage = ''
        if (promptCount > 0) {
            confirmMessage = t('aiprompt.deleteConfirm.dishWithPrompts', { name: collectionName, count: promptCount })
        } else {
            confirmMessage = t('aiprompt.deleteConfirm.dishWithoutPrompts', { name: collectionName })
        }
        
        // 儲存待刪除餐盤資訊和確認訊息
        selectedCollectionToDelete.value = collection
        deleteConfirmMessage.value = confirmMessage
        showDeleteCollectionConfirm.value = true
        
    } catch (error) {
        console.error('檢查餐盤指令數量失敗:', error)
        Message.error(t('aiprompt.messages.checkFailed', { error: error.message || t('aiprompt.errors.unknownError') }))
    }
}

// 關閉刪除餐盤確認彈窗
const closeDeleteCollectionConfirm = () => {
    showDeleteCollectionConfirm.value = false
    selectedCollectionToDelete.value = null
    deleteConfirmMessage.value = ''
    isDeletingItem.value = false
}

// 關閉刪除指令確認彈窗
const closeDeletePromptConfirm = () => {
    showDeletePromptConfirm.value = false
    selectedPromptToDelete.value = null
    deleteConfirmMessage.value = ''
    isDeletingItem.value = false
}

// 執行刪除餐盤的實際邏輯
const executeDeleteCollection = async () => {
    const collection = selectedCollectionToDelete.value
    if (!collection) return
    
    isDeletingItem.value = true
    
    try {
        // 執行刪除操作
        console.log('執行刪除餐盤:', collection.id)
        const collectionName = collection.collectionName || collection.name
        
        const deleteResponse = await aipromptAPI.deleteCollection({
            id: collection.id
        })
        
        console.log('刪除餐盤回應:', deleteResponse)
        
        // 檢查回應格式
        if (deleteResponse && deleteResponse.status === 200) {
            // 顯示詳細的刪除結果訊息
            const deletedPromptCount = deleteResponse.data?.deletedPromptCount || 0
            let successMessage = deleteResponse.message
            
            if (!successMessage) {
                if (deletedPromptCount > 0) {
                    successMessage = t('aiprompt.successMessages.dishDeletedWithPrompts', { name: collectionName, count: deletedPromptCount })
                } else {
                    successMessage = t('aiprompt.successMessages.dishDeleted', { name: collectionName })
                }
            }
            
            Message.success(successMessage)
            
            // 重新載入餐盤列表
            await loadUserCollections()
            
            // 如果刪除的是當前選中的餐盤，清除選擇
            if (selectedCollection.value?.id === collection.id) {
                selectedCollection.value = null
                currentView.value = 'official'
                collectionPrompts.value = []
            }
            
            // 清除待刪除餐盤資訊
            closeDeleteCollectionConfirm()
            
        } else {
            throw new Error(deleteResponse.message || '刪除餐盤失敗')
        }
        
    } catch (error) {
        console.error('刪除餐盤錯誤:', error)
        
        // 處理特定的錯誤情況
        if (error.message && error.message.includes('404')) {
            Message.error(t('aiprompt.errors.dishNotExistsOrDeleted'))
        } else if (error.message && error.message.includes('403')) {
            Message.error(t('aiprompt.errors.noPermissionToDelete'))
        } else {
            Message.error(t('aiprompt.errors.deleteDishFailed', { error: error.message || t('aiprompt.errors.unknownError') }))
        }
        
        // 清除待刪除餐盤資訊
        closeDeleteCollectionConfirm()
    }
}

// 顯示添加指令對話框
const showAddPromptDialog = () => {
    if (!selectedCollection.value) {
        Message.warning(t('aiprompt.messages.pleaseSelectDishFirst'))
        return
    }
    
    if (!isLogin.value) {
        Message.warning(t('aiprompt.messages.pleaseLoginToAddPrompt'))
        return
    }
    
    // 重置表單資料
    resetPromptForm()
    showAddPromptForm.value = true
}

// 關閉添加指令對話框
const closeAddPromptDialog = () => {
    showAddPromptForm.value = false
    isCreatingPrompt.value = false
    resetPromptForm()
}

// 重置指令表單
const resetPromptForm = () => {
    newPrompt.category = ''
    newPrompt.purpose = ''
    newPrompt.usage = ''
    newPrompt.sourceData = ''
    newPrompt.sourceDataType = 'text'
    newPrompt.conditionalPrompt = ''
    newPrompt.expectedOutput = ''
    newPrompt.followUpActions = ''
    newPrompt.personalNotes = ''
}

// 處理從 HT 分享指令到餐盤
const handlePromptShare = async (payload) => {
    console.log('處理指令分享:', payload);
    
    if (!payload) {
        Message.error(t('aiprompt.shareToPlate.noPromptData'));
        return;
    }
    
    // 檢查用戶是否登入
    if (!isLogin.value) {
        Message.warning(t('aiprompt.shareToPlate.loginRequired'));
        return;
    }
    
    // 載入用戶餐盤 (如果還沒載入)
    if (userCollections.value.length === 0) {
        await loadUserCollections();
    }
    
    // 檢查是否有餐盤
    if (userCollections.value.length === 0) {
        Message.warning(t('aiprompt.shareToPlate.noPlatesAvailable'));
        return;
    }
    
    // 解析並設定分享的指令資料 (只包含有值的欄位)
    const sharedData = {
        name: payload.name || '未命名指令',
        category: payload.category ? mapOfficialCategoryToValue(payload.category.toLowerCase()) : 'score', // 確保小寫並對應 categoryOptions
        conditionalPrompt: payload.conditionalPrompt || ''

    };
    
    // 只有在有值的情況下才添加額外欄位
    if (payload.usage) sharedData.usage = payload.usage;
    if (payload.sourceData) sharedData.sourceData = payload.sourceData;
    if (payload.expectedOutput) sharedData.expectedOutput = payload.expectedOutput;
    if (payload.followUpActions) sharedData.followUpActions = payload.followUpActions;
    if (payload.personalNotes) sharedData.personalNotes = payload.personalNotes;
    if (payload.sourceDataType) sharedData.sourceDataType = payload.sourceDataType.toLowerCase();
    
    sharePromptData.value = sharedData;

    // 如果還沒選擇餐盤，自動選擇預設或第一個
    if (!selectedPlateForShare.value && userCollections.value.length > 0) {
        const defaultPlate = userCollections.value.find(collection =>
            collection.tags && collection.tags.includes('default')
        )
        selectedPlateForShare.value = defaultPlate || userCollections.value[0]
    }

    // 顯示選擇餐盤彈窗
    showSelectPlateModal.value = true;
};

// 下拉選單會自動處理選擇，不需要額外的選擇方法

// 關閉選擇餐盤彈窗
const closeSelectPlateModal = () => {
    showSelectPlateModal.value = false;
    selectedPlateForShare.value = null;
    sharePromptData.value = null;
    isAddingToPlate.value = false;
};

// 將指令加入到選定的餐盤
const addPromptToSelectedPlate = async () => {
    if (!selectedPlateForShare.value || !sharePromptData.value) {
        Message.error(t('aiprompt.messages.pleaseSelectPlateAndConfirmData'));
        return;
    }
    
    isAddingToPlate.value = true;
    
    try {
        // 準備指令資料
        const promptData = {
            collectionId: selectedPlateForShare.value.id,
            category: sharePromptData.value.category || '其他',
            purpose: sharePromptData.value.name,
            creator: {
                id: profile.teammodelId,
                name: profile.name || '匿名用戶'
            },
            usage: sharePromptData.value.usage || '',
            sourceData: sharePromptData.value.sourceData || '',
            sourceDataType: sharePromptData.value.sourceDataType || 'text',
            conditionalPrompt: sharePromptData.value.conditionalPrompt || '',
            expectedOutput: sharePromptData.value.expectedOutput || '',
            followUpActions: sharePromptData.value.followUpActions || '',
            estimatedTimeSaved: 0,
            personalNotes: sharePromptData.value.personalNotes || '',
            sourceType: 'personal'
        };

        // 呼叫 API 創建指令
        const response = await aipromptAPI.createPrompt(promptData);
        
        // 檢查回應結構 - API 回傳 { status: 201, data: { id: "xxx", ... } }
        if (response && (response.data?.id || response.id)) {
            Message.success(t('aiprompt.messages.promptAddedToPlate', { plateName: selectedPlateForShare.value.collectionName }));

            // 儲存目標餐盤引用，避免關閉彈窗後丟失
            const targetPlate = selectedPlateForShare.value;

            // 關閉彈窗
            closeSelectPlateModal();

            // 自動跳轉到加入指令的餐盤，提升使用者體驗
            await selectCustomCollection(targetPlate);

            // 重新載入該餐盤的指令列表以顯示新加入的指令
            await loadCollectionPrompts(targetPlate.id);
        } else {
            throw new Error(response?.message || response?.data?.message || '加入指令失敗');
        }
        
    } catch (error) {
        console.error('加入指令到餐盤失敗:', error);
        Message.error(t('aiprompt.messages.addPromptToPlateFailed', { error: error.message || t('aiprompt.errors.unknownError') }));
    } finally {
        isAddingToPlate.value = false;
    }
};

// 創建新指令
const createNewPrompt = async () => {
    if (!isPromptFormValid.value) {
        Message.warning(t('aiprompt.messages.pleaseFillRequiredFields'))
        return
    }
    
    if (!selectedCollection.value || !profile.teammodelId) {
        Message.error(t('aiprompt.messages.missingRequiredInfo'))
        return
    }
    
    isCreatingPrompt.value = true
    
    try {
        const promptData = {
            collectionId: selectedCollection.value.id,
            category: newPrompt.category.trim(),
            purpose: newPrompt.purpose.trim(),
            creator: {
                id: profile.teammodelId,
                name: profile.name || '匿名用戶'
            },
            usage: newPrompt.usage.trim() || '',
            sourceData: newPrompt.sourceData.trim() || '',
            sourceDataType: newPrompt.sourceDataType || 'text',
            conditionalPrompt: newPrompt.conditionalPrompt.trim(),
            expectedOutput: newPrompt.expectedOutput.trim() || '',
            followUpActions: newPrompt.followUpActions.trim() || '',
            estimatedTimeSaved: 0,
            personalNotes: newPrompt.personalNotes.trim() || '',
            sourceType: 'personal'
        }
        
        console.log('創建指令資料:', promptData)
        
        const response = await aipromptAPI.createPrompt(promptData)
        
        console.log('新建指令回應:', response)
        
        // 檢查回應結構 - API 回傳 { status: 201, data: { id: "xxx", ... } }
        if (response && (response.data?.id || response.id)) {
            Message.success(t('aiprompt.messages.promptAdded'))
            closeAddPromptDialog()
            
            // 重新載入當前餐盤的指令
            await loadCollectionPrompts(selectedCollection.value.id)
        } else {
            throw new Error(response?.message || response?.data?.message || '添加指令失敗')
        }
        
    } catch (error) {
        console.error('創建指令錯誤:', error)
        Message.error(t('aiprompt.messages.addPromptFailed', { error: error.message || t('aiprompt.errors.unknownError') }))
    } finally {
        isCreatingPrompt.value = false
    }
}

// 載入餐盤指令
const loadCollectionPrompts = async (collectionId) => {
    if (!collectionId) {
        collectionPrompts.value = []
        return
    }
    
    isLoadingPrompts.value = true
    
    try {
        const response = await aipromptAPI.getPromptsByCollection({
            collectionId: collectionId
        })
        
        if (response && response.data) {
            collectionPrompts.value = response.data
            // 初始化自訂餐盤篩選器
            customFilteredPrompts.value = collectionPrompts.value
            customSelectedFilter.value = 'showAll'
            console.log('載入餐盤指令成功:', collectionPrompts.value)
        } else {
            collectionPrompts.value = []
            customFilteredPrompts.value = []
        }
        
    } catch (error) {
        console.error('載入餐盤指令失敗:', error)
        collectionPrompts.value = []
        customFilteredPrompts.value = []
    } finally {
        isLoadingPrompts.value = false
    }
}

// 編輯指令
const editPrompt = (prompt) => {
    console.log('編輯指令:', prompt)
    
    // 儲存原始資料用於變更比較
    originalPromptData.value = {
        id: prompt.id,
        category: prompt.category || '',
        purpose: prompt.purpose || '',
        usage: prompt.usage || '',
        sourceData: prompt.sourceData || '',
        sourceDataType: prompt.sourceDataType || 'text',
        conditionalPrompt: prompt.conditionalPrompt || '',
        expectedOutput: prompt.expectedOutput || '',
        followUpActions: prompt.followUpActions || '',
        personalNotes: prompt.personalNotes || ''
    }
    
    // 填充編輯表單數據
    editingPrompt.id = prompt.id
    editingPrompt.category = prompt.category || ''
    editingPrompt.purpose = prompt.purpose || ''
    editingPrompt.usage = prompt.usage || ''
    editingPrompt.sourceData = prompt.sourceData || ''
    editingPrompt.sourceDataType = prompt.sourceDataType || 'text'
    editingPrompt.conditionalPrompt = prompt.conditionalPrompt || ''
    editingPrompt.expectedOutput = prompt.expectedOutput || ''
    editingPrompt.followUpActions = prompt.followUpActions || ''
    editingPrompt.personalNotes = prompt.personalNotes || ''

    showEditPromptForm.value = true
}

// 關閉編輯指令對話框
const closeEditPromptDialog = () => {
    showEditPromptForm.value = false
    isUpdatingPrompt.value = false
    resetEditPromptForm()
}

// 重置編輯指令表單
const resetEditPromptForm = () => {
    editingPrompt.id = ''
    editingPrompt.category = ''
    editingPrompt.purpose = ''
    editingPrompt.usage = ''
    editingPrompt.sourceData = ''
    editingPrompt.sourceDataType = 'text'
    editingPrompt.conditionalPrompt = ''
    editingPrompt.expectedOutput = ''
    editingPrompt.followUpActions = ''
    editingPrompt.personalNotes = ''
}

// 檢查是否有資料變更
const hasDataChanged = () => {
    const currentData = {
        category: editingPrompt.category.trim(),
        purpose: editingPrompt.purpose.trim(),
        usage: editingPrompt.usage.trim(),
        sourceData: editingPrompt.sourceData.trim(),
        sourceDataType: editingPrompt.sourceDataType || 'text',
        conditionalPrompt: editingPrompt.conditionalPrompt.trim(),
        expectedOutput: editingPrompt.expectedOutput.trim(),
        followUpActions: editingPrompt.followUpActions.trim(),
        personalNotes: editingPrompt.personalNotes.trim()
    }

    const originalData = {
        category: (originalPromptData.value.category || '').trim(),
        purpose: (originalPromptData.value.purpose || '').trim(),
        usage: (originalPromptData.value.usage || '').trim(),
        sourceData: (originalPromptData.value.sourceData || '').trim(),
        sourceDataType: originalPromptData.value.sourceDataType || 'text',
        conditionalPrompt: (originalPromptData.value.conditionalPrompt || '').trim(),
        expectedOutput: (originalPromptData.value.expectedOutput || '').trim(),
        followUpActions: (originalPromptData.value.followUpActions || '').trim(),
        personalNotes: (originalPromptData.value.personalNotes || '').trim()
    }
    
    // 比較每個欄位是否有變更
    return Object.keys(currentData).some(key => currentData[key] !== originalData[key])
}

// 更新指令
const updatePrompt = async () => {
    if (!isEditPromptFormValid.value) {
        Message.warning(t('aiprompt.messages.pleaseFillRequiredFields'))
        return
    }
    
    if (!editingPrompt.id) {
        Message.error(t('aiprompt.messages.promptIdError'))
        return
    }
    
    // 檢查是否有資料變更
    if (!hasDataChanged()) {
        Message.info(t('aiprompt.messages.noDataChanges'))
        closeEditPromptDialog()
        return
    }
    
    isUpdatingPrompt.value = true
    
    try {
        // 只送出有變更的欄位
        const updateData = {
            id: editingPrompt.id,
            purpose: editingPrompt.purpose.trim()
        }
        
        // 檢查每個欄位是否有變更，只送出有變更的欄位
        const currentCategory = editingPrompt.category.trim()
        const originalCategory = (originalPromptData.value.category || '').trim()
        if (currentCategory !== originalCategory) {
            updateData.category = currentCategory
        }
        
        const currentUsage = editingPrompt.usage.trim()
        const originalUsage = (originalPromptData.value.usage || '').trim()
        if (currentUsage !== originalUsage) {
            updateData.usage = currentUsage
        }
        
        const currentSourceData = editingPrompt.sourceData.trim()
        const originalSourceData = (originalPromptData.value.sourceData || '').trim()
        if (currentSourceData !== originalSourceData) {
            updateData.sourceData = currentSourceData
        }

        const currentSourceDataType = editingPrompt.sourceDataType || 'text'
        const originalSourceDataType = originalPromptData.value.sourceDataType || 'text'
        if (currentSourceDataType !== originalSourceDataType) {
            updateData.sourceDataType = currentSourceDataType
        }

        const currentConditionalPrompt = editingPrompt.conditionalPrompt.trim()
        const originalConditionalPrompt = (originalPromptData.value.conditionalPrompt || '').trim()
        if (currentConditionalPrompt !== originalConditionalPrompt) {
            updateData.conditionalPrompt = currentConditionalPrompt
        }
        
        const currentExpectedOutput = editingPrompt.expectedOutput.trim()
        const originalExpectedOutput = (originalPromptData.value.expectedOutput || '').trim()
        if (currentExpectedOutput !== originalExpectedOutput) {
            updateData.expectedOutput = currentExpectedOutput
        }
        
        const currentFollowUpActions = editingPrompt.followUpActions.trim()
        const originalFollowUpActions = (originalPromptData.value.followUpActions || '').trim()
        if (currentFollowUpActions !== originalFollowUpActions) {
            updateData.followUpActions = currentFollowUpActions
        }
        
        const currentPersonalNotes = editingPrompt.personalNotes.trim()
        const originalPersonalNotes = (originalPromptData.value.personalNotes || '').trim()
        if (currentPersonalNotes !== originalPersonalNotes) {
            updateData.personalNotes = currentPersonalNotes
        }
        
        console.log('更新指令資料 (僅有變更的欄位):', updateData)
        
        const response = await aipromptAPI.updatePrompt(updateData)
        
        console.log('更新指令回應:', response)
        
        // 檢查回應格式 - API 回傳 { status: 200, data: { ... } }
        if (response && (response.data?.id || response.status === 200)) {
            Message.success(t('aiprompt.messages.promptUpdated'))
            closeEditPromptDialog()
            
            // 重新載入當前餐盤的指令
            if (selectedCollection.value) {
                await loadCollectionPrompts(selectedCollection.value.id)
            }
        } else {
            throw new Error('更新指令失敗')
        }
        
    } catch (error) {
        console.error('更新指令錯誤:', error)
        Message.error(t('aiprompt.messages.updatePromptFailed', { error: error.message || '未知錯誤' }))
    } finally {
        isUpdatingPrompt.value = false
    }
}

// 刪除指令
const deletePrompt = async (prompt) => {
    // 儲存待刪除指令資訊
    selectedPromptToDelete.value = prompt
    deleteConfirmMessage.value = t('aiprompt.deleteConfirm.prompt', { name: prompt.purpose })
    showDeletePromptConfirm.value = true
}

// 執行刪除指令的實際邏輯
const executeDeletePrompt = async () => {
    const prompt = selectedPromptToDelete.value
    if (!prompt) return
    
    isDeletingItem.value = true
    
    try {
        console.log('刪除指令:', prompt)
        
        // API 只需要 id 參數
        const response = await aipromptAPI.deletePrompt({
            id: prompt.id
        })
        
        console.log('刪除指令回應:', response)
        
        // 檢查回應格式 - API 回傳 { status: 200, message: "AI指令刪除成功" }
        if (response && (response.status === 200 || response.message)) {
            Message.success(response.message || t('aiprompt.successMessages.promptDeleted'))
            
            // 重新載入當前餐盤的指令
            if (selectedCollection.value) {
                await loadCollectionPrompts(selectedCollection.value.id)
            }
            
            // 清除待刪除指令資訊
            closeDeletePromptConfirm()
            
        } else {
            throw new Error('刪除指令失敗')
        }
        
    } catch (error) {
        console.error('刪除指令錯誤:', error)
        Message.error(t('aiprompt.errors.deletePromptFailed', { error: error.message || t('aiprompt.errors.unknownError') }))
        
        // 清除待刪除指令資訊
        closeDeletePromptConfirm()
    }
}


// 初始化認證
const initializeAuth = async () => {
    try {
        const res = await api.main.getTokenUnlogin()
        const exp = jwt_decode(res.auth_token).exp
        localStorage.setItem("auth_token", res.auth_token)
        localStorage.setItem("auth_token_expiry", exp)
    } catch (error) {
        console.error("Error fetching auth token:", error)
    }
}

// 檢查自動登入
const checkAutoLogin = async () => {
    const savedProfile = localStorage.getItem("profile")
    const savedToken = localStorage.getItem("access_token")
    const idToken = localStorage.getItem("id_token")
    
    if (savedProfile && savedToken) {
        try {
            const profileData = JSON.parse(savedProfile)
            isLogin.value = true
            profile.name = profileData.name
            profile.teammodelId = profileData.id || profileData.teammodelId
            profile.picture = profileData.picture
            store.setIsLogIn(true)
            
            // 如果有 id_token，解析並驗證用戶 ID
            if (idToken) {
                try {
                    const decoded = jwt_decode(idToken)
                    // 使用 token 中的用戶 ID 更新 profile
                    if (decoded.sub) {
                        profile.teammodelId = decoded.sub
                    }
                } catch (tokenError) {
                    console.error("Token decode error:", tokenError)
                }
            }
            
            // 檢查管理員權限和管理員用戶
            checkAdminStatus()
            checkAdminUser()
            
            // 登入成功後載入用戶餐盤
            await loadUserCollections()
            
            // 發送登入完成訊息到 HT
            sendLogInDoneToHT()
        } catch (error) {
            console.error("Auto login error:", error)
        }
    }
}

// 處理 OAuth 回調
const handleOAuthCallback = async () => {
    const url = new URL(window.location.href)
    const code = url.searchParams.get("code")
    
    if (code) {
        try {
            const res = await api.main.getToken()
            localStorage.setItem("id_token", res.id_token)
            localStorage.setItem("access_token", res.access_token)
            localStorage.setItem("expires_in", res.expires_in)
            
            const decoded = jwt_decode(res.id_token)
            
            isLogin.value = true
            store.setIsLogIn(true)
            profile.name = decoded.name
            profile.teammodelId = decoded.sub
            profile.picture = decoded.picture
            
            localStorage.setItem("profile", JSON.stringify({
                name: profile.name,
                id: profile.teammodelId,
                picture: profile.picture,
            }))
            
            // 清除 URL 中的 code 參數
            const newUrl = new URL(window.location.href)
            newUrl.searchParams.delete('code')
            newUrl.searchParams.delete('state')
            window.history.replaceState({}, document.title, newUrl.toString())
            
            Message.success(t('aiprompt.messages.loginSuccess'))
            
            // 檢查管理員權限和管理員用戶
            checkAdminStatus()
            checkAdminUser()
            
            // OAuth 登入成功後載入用戶餐盤
            await loadUserCollections()
            
            // 發送登入完成訊息到 HT
            sendLogInDoneToHT()
        } catch (error) {
            console.error("OAuth callback error:", error)
            Message.error(t('aiprompt.messages.loginFailed'))
        }
    }
}

// 點擊外部關閉用戶選單
const handleClickOutside = (event) => {
    if (!event.target.closest('.user-section')) {
        showUserMenu.value = false
    }
}

// 監聽語系變化，重新載入資料
watch(locale, async (newLocale, oldLocale) => {
    if (newLocale !== oldLocale) {
        console.log(`語系變更: ${oldLocale} -> ${newLocale}`)
        await loadAIPromptData()
    }
}, { immediate: false })

// HT WebView 通訊橋接器 (參考 molegame-play 的嚴謹作法)
const MessageBridge = {
    ENV: {
        WEBVIEW: 'webview',
        WEB: 'web'
    },
    // 當前環境
    currentEnv: 'webview',
    // 初始化
    init() {
        this.currentEnv = window?.chrome?.webview ? this.ENV.WEBVIEW : this.ENV.WEB;
        return this;
    },
    // 發送消息
    postMessage(data, origin = '*') {
        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.postMessage(data);
        } else {
            window.postMessage(data, origin);
        }
    },
    // 添加消息監聽器
    addEventListener(callback) {
        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.addEventListener('message', event => {
                callback(event);
            });
        } else {
            window.addEventListener('message', event => {
                callback(event);
            });
        }
    }
};

// 初始化消息橋接器
const messageBridge = MessageBridge.init();

// WebView 環境檢測
const isWebView = computed(() => {
    return messageBridge.currentEnv === MessageBridge.ENV.WEBVIEW;
});

// 發送 AI Prompt 資料到 HT
const sendAIPromptToHT = (promptData) => {
    const messageData = {
        action: 'Send.AIPrompt',
        data: {...promptData }
    };
    
    console.log('發送 AI Prompt 到 HT:', messageData);
    messageBridge.postMessage(messageData);
};

const sendInitToHT = () => {
    const messageData = {
        action: 'Send.InitPromtLib'
    };
    
    console.log('發送 InitPromtLib 到 HT:', messageData);
    messageBridge.postMessage(messageData);
};

const sendLogInDoneToHT = () => {
    const messageData = {
        action: 'Send.Login'
    };
    
    console.log('發送 已經 Login 到 HT:', messageData);
    messageBridge.postMessage(messageData);
};

const openEditDishModal = () => {
    // 打開編輯餐盤 modal
    if (selectedCollection.value) {
        showEditCollectionDialog(selectedCollection.value);
    }
};

// 組件掛載時執行
onMounted(async () => {
    // 初始化認證
    await initializeAuth()
    
    // 檢查自動登入
    await checkAutoLogin()
    
    // 添加窗口大小監聽器
    window.addEventListener('resize', resizeListener)
    
    // 處理 OAuth 回調
    await handleOAuthCallback()
    
    // 添加點擊外部事件監聽
    document.addEventListener('click', handleClickOutside)
    
    // 載入 AI Prompt 資料
    await loadAIPromptData()
    
    // 載入用戶餐盤（如果已登入）
    await loadUserCollections()
    
    // 初始化 HT WebView 通訊監聽 (參考 MindMap.vue 的 initialMindMap)
    console.log('🔧 MessageBridge 環境檢測:', {
        currentEnv: messageBridge.currentEnv,
        hasWebView: !!window?.chrome?.webview,
        hasWindow: !!window
    });
    
    
    messageBridge.addEventListener(async (event) => {
        
        try {
            let msg = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
            console.log('✅收到訊息:', msg);

            if (msg.action === "initialPromptLibary") {
                console.log('收到initialPromptLibary');
                // 接收更新的 token (參考 initialMindMap 作法)
                if (msg.parameters?.access_Token) {
                    setupUserAuth(msg.parameters);
                    console.log('更新 access_token 從 HT');
                }
            }
            else if (msg.action === "PromptLibary.Share") {
                console.log('收到PromptLibary.Share');
                handlePromptShare(msg.payload);
            }


        } catch (error) {
            console.error('處理 HT WebView 訊息時發生錯誤:', error);
        }
    });
    
    console.log('AI Prompt WebView 通訊已初始化 - 環境:', messageBridge.currentEnv);
    sendInitToHT()

})

// 組件卸載時執行
onUnmounted(() => {
    // 移除窗口大小監聽器
    window.removeEventListener('resize', resizeListener)
})
</script>

<style lang="less">
@import "../assets/AIPromptHome.less";
</style>